import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from './services/api';
import EmailManager from './components/EmailManager';
import AdminLayout from './components/AdminLayout';
import BrandingConfigManager from './components/BrandingConfigManager';
import UploadConfigManager from './components/UploadConfigManager';
import AreaConfigManager from './components/AreaConfigManager';
import Papa from 'papaparse';
import ImageEditor from './components/ImageEditor';
import { useDropzone } from 'react-dropzone';

import { parseJsonArray } from './utils/parseJsonArray';
import MapPicker from './components/MapPicker';
import AdminAnalytics from './components/AdminAnalytics';
import PendingExtraNotesManager from './components/PendingExtraNotesManager';
import ErrorLogViewer from './components/ErrorLogViewer';
import AnalyticsPage from './components/AnalyticsPage';
import SystemInfoPanel from './components/SystemInfoPanel';
import { validateForm } from './services/validation';
import { INCIDENT_TYPE_GROUPS } from './incidentTypes';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import useDefaultMapConfig from './hooks/useDefaultMapConfig';
import { getMurrayRoadMapConfig } from './config/murrayRoadMapConfig';

// Google Maps libraries constant to prevent LoadScript reloading
const GOOGLE_MAPS_LIBRARIES = [];

// Database Management Component
function DatabaseManagementTab({ user: _user }) {
  const [dbStats, setDbStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [confirmModal, setConfirmModal] = useState({
    show: false,
    title: '',
    message: '',
    action: null,
    type: '',
  });
  const [lastOperation, setLastOperation] = useState({ message: '', type: '', timestamp: null });
  const [restoreFile, setRestoreFile] = useState(null);
  const [tableStatus, setTableStatus] = useState(null);
  const [tableStatusLoading, setTableStatusLoading] = useState(false);
  const [syncingTables, setSyncingTables] = useState(false);

  // Fetch database statistics
  const fetchDbStats = async () => {
    try {
      const response = await api.getDatabaseStats();
      setDbStats(response);
    } catch (error) {
      console.error('Error fetching database stats:', error);
    }
  };

  // Fetch table status
  const fetchTableStatus = async () => {
    setTableStatusLoading(true);
    try {
      const response = await api.getTableStatus();
      setTableStatus(response);
    } catch (error) {
      console.error('Error fetching table status:', error);
      setTableStatus({ error: error.message });
    } finally {
      setTableStatusLoading(false);
    }
  };

  // Sync missing tables
  const handleSyncTables = async (specificTables = null) => {
    setSyncingTables(true);
    try {
      const response = await api.syncTables(specificTables);
      setLastOperation({
        message: response.message || 'Tables synchronized successfully',
        type: 'success',
        timestamp: new Date(),
      });
      // Refresh table status after sync
      await fetchTableStatus();
    } catch (error) {
      setLastOperation({
        message: error.message || 'Error syncing tables',
        type: 'error',
        timestamp: new Date(),
      });
    } finally {
      setSyncingTables(false);
    }
  };

  useEffect(() => {
    fetchDbStats();
    fetchTableStatus();
  }, []);

  // Database operation handlers
  const handleDatabaseOperation = async (operation, _params = {}) => {
    setLoading(true);
    try {
      let response;
      switch (operation) {
        case 'clear-reports':
          response = await api.clearAllReports();
          break;
        case 'clear-unapproved-reports':
          response = await api.clearUnapprovedReports();
          break;
        case 'clear-approved-reports':
          response = await api.clearApprovedReports();
          break;
        case 'reset-auto-increment':
          response = await api.resetAutoIncrement();
          break;
        case 'populate-sample-data':
          response = await api.populateSampleData();
          break;
        case 'vacuum-database':
          response = await api.optimizeDatabase();
          break;
        case 'backup-database':
          response = await api.backupDatabase('json');
          break;
        case 'backup-database-sql':
          response = await api.backupDatabase('sql');
          break;
        case 'restore-database':
          if (!restoreFile) {
            throw new Error('Please select a backup file to restore');
          }
          response = await api.restoreDatabase(restoreFile);
          setRestoreFile(null); // Clear file after successful restore
          break;
        default:
          throw new Error(`Unknown operation: ${operation}`);
      }

      setLastOperation({
        message: response.message || `${operation} completed successfully`,
        type: 'success',
        timestamp: new Date(),
      });
      await fetchDbStats(); // Refresh stats
    } catch (error) {
      setLastOperation({
        message: error.message || `Error performing ${operation}`,
        type: 'error',
        timestamp: new Date(),
      });
    } finally {
      setLoading(false);
      setConfirmModal({ show: false });
    }
  };

  const showConfirmation = (title, message, action, type = 'danger') => {
    setConfirmModal({
      show: true,
      title,
      message,
      action,
      type,
    });
  };

  const databaseOperations = [
    {
      id: 'clear-all-reports',
      title: 'Clear All Reports',
      description: 'Delete all incident reports from the database',
      icon: '🗑️',
      type: 'danger',
      action: () =>
        showConfirmation(
          'Clear All Reports',
          'This will permanently delete ALL incident reports from the database. This action cannot be undone. Are you sure?',
          () => handleDatabaseOperation('clear-reports'),
        ),
    },
    {
      id: 'clear-unapproved-reports',
      title: 'Clear Unapproved Reports',
      description: 'Delete only unapproved/pending incident reports',
      icon: '🔄',
      type: 'warning',
      action: () =>
        showConfirmation(
          'Clear Unapproved Reports',
          'This will delete all unapproved/pending reports. Approved reports will remain. Continue?',
          () => handleDatabaseOperation('clear-unapproved-reports'),
          'warning',
        ),
    },
    {
      id: 'clear-approved-reports',
      title: 'Clear Approved Reports',
      description: 'Delete only approved incident reports',
      icon: '✅',
      type: 'warning',
      action: () =>
        showConfirmation(
          'Clear Approved Reports',
          'This will delete all approved reports. Pending reports will remain. Continue?',
          () => handleDatabaseOperation('clear-approved-reports'),
          'warning',
        ),
    },
    {
      id: 'reset-auto-increment',
      title: 'Reset Auto-Increment',
      description: 'Reset the report ID counter to start from 1',
      icon: '🔢',
      type: 'info',
      action: () =>
        showConfirmation(
          'Reset Auto-Increment',
          'This will reset the report ID counter to 1. New reports will start from ID 1. Continue?',
          () => handleDatabaseOperation('reset-auto-increment'),
          'info',
        ),
    },
    {
      id: 'populate-sample-data',
      title: 'Populate Sample Data',
      description: 'Add sample incident reports for testing',
      icon: '📊',
      type: 'success',
      action: () =>
        showConfirmation(
          'Populate Sample Data',
          'This will add sample incident reports to the database. Continue?',
          () => handleDatabaseOperation('populate-sample-data'),
          'success',
        ),
    },
    {
      id: 'vacuum-database',
      title: 'Optimize Database',
      description: 'Clean up and optimize database performance',
      icon: '⚡',
      type: 'info',
      action: () =>
        showConfirmation(
          'Optimize Database',
          'This will clean up and optimize the database. This is safe and may improve performance. Continue?',
          () => handleDatabaseOperation('vacuum-database'),
          'info',
        ),
    },
    {
      id: 'backup-database',
      title: 'Backup Database (JSON)',
      description: 'Download a complete backup of all database data in JSON format',
      icon: '💾',
      type: 'success',
      action: () => handleDatabaseOperation('backup-database'),
    },
    {
      id: 'backup-database-sql',
      title: 'Backup Database (SQL)',
      description: 'Download a complete backup of all database data as SQL dump (ZIP file)',
      icon: '🗄️',
      type: 'success',
      action: () => handleDatabaseOperation('backup-database-sql'),
    },
    {
      id: 'restore-database',
      title: 'Restore Database',
      description: 'Restore database from a backup file',
      icon: '🔄',
      type: 'warning',
      action: () => {
        // Create file input element
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.json';
        input.onchange = e => {
          const file = e.target.files[0];
          if (file) {
            setRestoreFile(file);
            showConfirmation(
              'Restore Database',
              `This will completely replace the current database with data from "${file.name}". ALL current data will be lost. This action cannot be undone. Are you absolutely sure?`,
              () => handleDatabaseOperation('restore-database'),
              'danger',
            );
          }
        };
        input.click();
      },
    },
  ];

  return (
    <section className="bg-white rounded-2xl shadow-soft p-8">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">🗄️ Database Management</h2>
        <p className="text-gray-600 mb-6">
          Manage database operations and maintenance. These operations are powerful and should be
          used with caution.
        </p>

        {/* Warning Banner */}
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <span className="text-red-600 text-xl">⚠️</span>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Danger Zone</h3>
              <p className="text-sm text-red-700 mt-1">
                These operations can permanently delete data. Always backup your database before
                performing destructive operations.
              </p>
            </div>
          </div>
        </div>

        {/* Database Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center">
              <span className="text-blue-600 text-2xl mr-3">📊</span>
              <div>
                <p className="text-sm font-medium text-blue-800">Total Reports</p>
                <p className="text-2xl font-bold text-blue-900">{dbStats.totalReports || 0}</p>
              </div>
            </div>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center">
              <span className="text-green-600 text-2xl mr-3">✅</span>
              <div>
                <p className="text-sm font-medium text-green-800">Approved</p>
                <p className="text-2xl font-bold text-green-900">{dbStats.approvedReports || 0}</p>
              </div>
            </div>
          </div>
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <div className="flex items-center">
              <span className="text-orange-600 text-2xl mr-3">⏳</span>
              <div>
                <p className="text-sm font-medium text-orange-800">Pending</p>
                <p className="text-2xl font-bold text-orange-900">{dbStats.pendingReports || 0}</p>
              </div>
            </div>
          </div>
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <div className="flex items-center">
              <span className="text-purple-600 text-2xl mr-3">👥</span>
              <div>
                <p className="text-sm font-medium text-purple-800">Total Users</p>
                <p className="text-2xl font-bold text-purple-900">{dbStats.totalUsers || 0}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Database Table Status */}
        <div className="mb-8 border border-gray-200 rounded-lg overflow-hidden">
          <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center">
              <span className="text-xl mr-2">🗃️</span>
              <h3 className="text-lg font-semibold text-gray-900">Database Tables</h3>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={fetchTableStatus}
                disabled={tableStatusLoading}
                className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded-md flex items-center gap-1 disabled:opacity-50"
              >
                {tableStatusLoading ? '⏳' : '🔄'} Refresh
              </button>
              {tableStatus?.summary?.missing > 0 && (
                <button
                  onClick={() => handleSyncTables()}
                  disabled={syncingTables}
                  className="px-3 py-1 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md flex items-center gap-1 disabled:opacity-50"
                >
                  {syncingTables ? '⏳ Syncing...' : '⚡ Create Missing Tables'}
                </button>
              )}
            </div>
          </div>

          {tableStatusLoading && !tableStatus ? (
            <div className="p-4 text-center text-gray-500">
              <span className="animate-pulse">Loading table status...</span>
            </div>
          ) : tableStatus?.error ? (
            <div className="p-4 bg-red-50 text-red-700">
              Error loading table status: {tableStatus.error}
            </div>
          ) : tableStatus?.tables ? (
            <>
              {/* Summary */}
              <div
                className={`px-4 py-2 text-sm ${tableStatus.summary.allPresent ? 'bg-green-50 text-green-800' : 'bg-yellow-50 text-yellow-800'}`}
              >
                {tableStatus.summary.allPresent ? (
                  <span>✅ All {tableStatus.summary.total} required tables are present</span>
                ) : (
                  <span>
                    ⚠️ {tableStatus.summary.missing} of {tableStatus.summary.total} tables are
                    missing
                  </span>
                )}
              </div>

              {/* Table List */}
              <div className="divide-y divide-gray-100">
                {tableStatus.tables.map(table => (
                  <div
                    key={table.name}
                    className={`px-4 py-2 flex items-center justify-between ${!table.exists ? 'bg-red-50' : ''}`}
                  >
                    <div className="flex items-center">
                      <span className={`mr-2 ${table.exists ? 'text-green-600' : 'text-red-600'}`}>
                        {table.exists ? '✓' : '✗'}
                      </span>
                      <div>
                        <span className="font-mono text-sm">{table.name}</span>
                        <span className="text-gray-500 text-xs ml-2">- {table.description}</span>
                      </div>
                    </div>
                    {!table.exists && (
                      <button
                        onClick={() => handleSyncTables([table.name])}
                        disabled={syncingTables}
                        className="px-2 py-1 text-xs bg-blue-100 hover:bg-blue-200 text-blue-700 rounded disabled:opacity-50"
                      >
                        Create
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="p-4 text-center text-gray-500">Click Refresh to check table status</div>
          )}
        </div>

        {/* Last Operation Status */}
        {lastOperation.timestamp && (
          <div
            className={`mb-6 p-4 rounded-lg border ${
              lastOperation.type === 'success'
                ? 'bg-green-50 border-green-200 text-green-800'
                : 'bg-red-50 border-red-200 text-red-800'
            }`}
          >
            <div className="flex items-center">
              <span className="mr-2">{lastOperation.type === 'success' ? '✅' : '❌'}</span>
              <div>
                <p className="font-medium">{lastOperation.message}</p>
                <p className="text-sm opacity-75">{lastOperation.timestamp.toLocaleString()}</p>
              </div>
            </div>
          </div>
        )}

        {/* File Selection Status */}
        {restoreFile && (
          <div className="mb-6 p-4 rounded-lg border bg-blue-50 border-blue-200 text-blue-800">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <span className="mr-2">📁</span>
                <div>
                  <p className="font-medium">Backup file selected for restore</p>
                  <p className="text-sm opacity-75">{restoreFile.name}</p>
                </div>
              </div>
              <button
                onClick={() => setRestoreFile(null)}
                className="text-blue-600 hover:text-blue-800 text-sm underline"
              >
                Clear
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Database Operations Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {databaseOperations.map(operation => (
          <div
            key={operation.id}
            className={`border rounded-xl p-6 transition-all duration-200 hover:shadow-lg ${
              operation.type === 'danger'
                ? 'border-red-200 bg-red-50 hover:bg-red-100'
                : operation.type === 'warning'
                  ? 'border-orange-200 bg-orange-50 hover:bg-orange-100'
                  : operation.type === 'success'
                    ? 'border-green-200 bg-green-50 hover:bg-green-100'
                    : 'border-blue-200 bg-blue-50 hover:bg-blue-100'
            }`}
          >
            <div className="text-center mb-4">
              <span className="text-4xl mb-2 block">{operation.icon}</span>
              <h3
                className={`text-lg font-semibold mb-2 ${
                  operation.type === 'danger'
                    ? 'text-red-800'
                    : operation.type === 'warning'
                      ? 'text-orange-800'
                      : operation.type === 'success'
                        ? 'text-green-800'
                        : 'text-blue-800'
                }`}
              >
                {operation.title}
              </h3>
              <p
                className={`text-sm mb-4 ${
                  operation.type === 'danger'
                    ? 'text-red-700'
                    : operation.type === 'warning'
                      ? 'text-orange-700'
                      : operation.type === 'success'
                        ? 'text-green-700'
                        : 'text-blue-700'
                }`}
              >
                {operation.description}
              </p>
            </div>
            <button
              onClick={operation.action}
              disabled={loading}
              className={`w-full py-2 px-4 rounded-lg font-medium transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${
                operation.type === 'danger'
                  ? 'bg-red-600 hover:bg-red-700 text-white'
                  : operation.type === 'warning'
                    ? 'bg-orange-600 hover:bg-orange-700 text-white'
                    : operation.type === 'success'
                      ? 'bg-green-600 hover:bg-green-700 text-white'
                      : 'bg-blue-600 hover:bg-blue-700 text-white'
              }`}
            >
              {loading ? 'Processing...' : operation.title}
            </button>
          </div>
        ))}
      </div>

      {/* Confirmation Modal */}
      {confirmModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="text-center mb-6">
              <span
                className={`text-6xl mb-4 block ${
                  confirmModal.type === 'danger'
                    ? '🚨'
                    : confirmModal.type === 'warning'
                      ? '⚠️'
                      : confirmModal.type === 'success'
                        ? '✅'
                        : 'ℹ️'
                }`}
              ></span>
              <h3 className="text-xl font-bold text-gray-900 mb-2">{confirmModal.title}</h3>
              <p className="text-gray-600">{confirmModal.message}</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmModal({ show: false })}
                className="flex-1 py-2 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmModal.action}
                className={`flex-1 py-2 px-4 rounded-lg text-white font-medium transition-colors ${
                  confirmModal.type === 'danger'
                    ? 'bg-red-600 hover:bg-red-700'
                    : confirmModal.type === 'warning'
                      ? 'bg-orange-600 hover:bg-orange-700'
                      : confirmModal.type === 'success'
                        ? 'bg-green-600 hover:bg-green-700'
                        : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

// Admin Reports Map Component
function AdminReportsMap({
  reports,
  onReportSelect: _onReportSelect,
  selectedReportId,
  setSelectedReport: _setSelectedReport,
  openEditModal,
}) {
  const {
    defaultCenter,
    defaultZoom,
    mapRestrictions,
    loading: configLoading,
  } = useDefaultMapConfig();
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [mapZoom, setMapZoom] = useState(defaultZoom);
  const mapRef = useRef(null);

  // Google Maps API loader (same as main app)
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey:
      process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'AIzaSyDfJ-ekd6yt6eT6G2lkRDE4-R5rpxq1jLU',
    version: 'weekly',
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  // Update map center and zoom when config changes
  useEffect(() => {
    if (defaultCenter && defaultZoom) {
      setMapCenter(defaultCenter);
      setMapZoom(defaultZoom);
    }
  }, [defaultCenter, defaultZoom]);

  // Filter reports that have valid coordinates
  const reportsWithCoords = reports.filter(report => {
    // Check for coordinates in the nested coordinates object
    const coords = report.coordinates;
    const hasLat = coords && coords.lat && !isNaN(parseFloat(coords.lat));
    const hasLng = coords && coords.lng && !isNaN(parseFloat(coords.lng));
    return hasLat && hasLng;
  });

  // Debug logging
  useEffect(() => {
    console.log('🔍 AdminReportsMap Component Rendered:', {
      totalReports: reports.length,
      reportsWithCoords: reportsWithCoords.length,
      componentMounted: true,
      mapRestrictions,
      mapCenter,
      mapZoom,
      configLoading,
    });

    if (reports.length > 0) {
      console.log('🔍 AdminReportsMap Debug:', {
        totalReports: reports.length,
        reportsWithCoords: reportsWithCoords.length,
        firstReport: reports[0],
        sampleReportCoords: {
          lat: reports[0]?.coordinates?.lat,
          lng: reports[0]?.coordinates?.lng,
          latType: typeof reports[0]?.coordinates?.lat,
          lngType: typeof reports[0]?.coordinates?.lng,
          coordinatesObject: reports[0]?.coordinates,
        },
        allReportFields: Object.keys(reports[0]),
        firstThreeReports: reports.slice(0, 3).map(r => ({
          id: r.id,
          location: r.location,
          coordinates: r.coordinates,
          hasValidCoords: r.coordinates && r.coordinates.lat && r.coordinates.lng,
        })),
      });
    }
  }, [reports, reportsWithCoords.length, mapRestrictions, mapCenter, mapZoom, configLoading]);

  const onMapLoad = useCallback(map => {
    mapRef.current = map;
  }, []);

  const onMapUnmount = useCallback(() => {
    mapRef.current = null;
  }, []);

  const handleMarkerClick = useCallback(
    report => {
      // Open the report edit modal (same as clicking "Edit" button)
      openEditModal(report);
    },
    [openEditModal],
  );

  // Use the exact same getMarkerIcon function as the main app
  const getMarkerIcon = (incidentType, isActive = false) => {
    // Make markers smaller to reduce crowding
    const baseSize = isActive ? 32 : 28;
    const totalHeight = baseSize + 8; // Add extra height for the point
    const anchorPoint = baseSize / 2;

    // Define colors for different incident categories
    const colors = {
      // Motor Vehicles - Red
      car: { fill: '#ef4444', stroke: '#dc2626' },
      // Motorcycles - Orange
      motorcycle: { fill: '#f97316', stroke: '#ea580c' },
      // Scooters - Teal
      scooter: { fill: '#14b8a6', stroke: '#0d9488' },
      // Trucks - Purple
      truck: { fill: '#8b5cf6', stroke: '#7c3aed' },
      // Bicycles - Blue
      bicycle: { fill: '#3b82f6', stroke: '#2563eb' },
      // Pedestrians - Green
      pedestrian: { fill: '#10b981', stroke: '#059669' },
      // Animals - Brown
      animal: { fill: '#a16207', stroke: '#92400e' },
      // Near Misses - Yellow
      nearMiss: { fill: '#eab308', stroke: '#ca8a04' },
      // Dangerous Driving - Dark Red
      dangerous: { fill: '#dc2626', stroke: '#b91c1c' },
      // Other - Gray
      other: { fill: '#6b7280', stroke: '#4b5563' },
    };

    // Determine color based on incident type
    let color = colors.other;
    if (incidentType) {
      // Prioritize bicycle and pedestrian colors for safety awareness
      if (incidentType.includes('bicycle')) {
        color = colors.bicycle;
      } else if (incidentType.includes('pedestrian')) {
        color = colors.pedestrian;
      } else if (incidentType.includes('car') || incidentType.includes('vehicle')) {
        color = colors.car;
      } else if (incidentType.includes('motorcycle')) {
        color = colors.motorcycle;
      } else if (incidentType.includes('scooter')) {
        color = colors.scooter;
      } else if (incidentType.includes('truck')) {
        color = colors.truck;
      } else if (incidentType.includes('animal')) {
        color = colors.animal;
      } else if (incidentType.includes('near-miss')) {
        color = colors.nearMiss;
      } else if (
        incidentType.includes('dangerous') ||
        incidentType.includes('speeding') ||
        incidentType.includes('reckless') ||
        incidentType.includes('road-rage')
      ) {
        color = colors.dangerous;
      }
    }

    // Generate SVG with appropriate icon based on incident type
    let iconPath = '';
    let iconColor = '#ffffff';

    if (incidentType) {
      // Prioritize bicycle and pedestrian icons for safety awareness
      if (incidentType.includes('bicycle')) {
        iconPath =
          'M15.5 5.5c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zM5 12c-2.8 0-5 2.2-5 5s2.2 5 5 5 5-2.2 5-5-2.2-5-5-5zm0 8.5c-1.9 0-3.5-1.6-3.5-3.5s1.6-3.5 3.5-3.5 3.5 1.6 3.5 3.5-1.6 3.5-3.5 3.5zm5.8-10l2.4-2.4.8.8c1.3 1.3 3 2.1 5.1 2.1V9c-1.5 0-2.7-.6-3.6-1.5l-1.9-1.9c-.5-.4-1-.6-1.6-.6s-1.1.2-1.4.6L7.8 8.4c-.4.4-.6.9-.6 1.4 0 .6.2 1.1.6 1.4L11 14v5h2v-6.2l-2.2-2.3zM19 12c-2.8 0-5 2.2-5 5s2.2 5 5 5 5-2.2 5-5-2.2-5-5-5zm0 8.5c-1.9 0-3.5-1.6-3.5-3.5s1.6-3.5 3.5-3.5 3.5 1.6 3.5 3.5-1.6 3.5-3.5 3.5z';
      } else if (incidentType.includes('pedestrian')) {
        iconPath =
          'M12 2c1.1 0 2 .9 2 2s-.9 2-2 2-2-.9-2-2 .9-2 2-2zm9 7h-6v13h-2v-6h-2v6H9V9H3V7h18v2z';
      } else if (incidentType.includes('car') || incidentType.includes('vehicle')) {
        iconPath =
          'M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.22.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z';
      } else if (incidentType.includes('motorcycle')) {
        iconPath =
          'M19.44 9.03L15.41 5H11v2h3.59l2 2H5c-2.8 0-5 2.2-5 5s2.2 5 5 5c2.46 0 4.45-1.69 4.9-4h.65c.45 2.31 2.44 4 4.9 4 2.8 0 5-2.2 5-5 0-2.65-2.15-4.8-4.56-4.97zM7.82 15C7.4 16.15 6.28 17 5 17c-1.66 0-3-1.34-3-3s1.34-3 3-3c1.28 0 2.4.85 2.82 2H7.82zM19 17c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3z';
      } else if (incidentType.includes('scooter')) {
        iconPath =
          'M12 2C8.13 2 5 5.13 5 9c0 1.74.69 3.32 1.81 4.48L7 15h10l.19-1.52C18.31 12.32 19 10.74 19 9c0-3.87-3.13-7-7-7zM8.5 9c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5-.67 1.5-1.5 1.5-1.5-.67-1.5-1.5zm7 0c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5-.67 1.5-1.5 1.5-1.5-.67-1.5-1.5zM12 17c-1.1 0-2-.9-2-2h4c0 1.1-.9 2-2 2z';
      } else if (incidentType.includes('truck')) {
        iconPath =
          'M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h4c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm13.5-9l1.96 2.5H17V9.5h2.5zm-1.5 9c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z';
      } else if (incidentType.includes('animal')) {
        iconPath = 'M4.5 9.5m-2.5 0a2.5 2.5 0 1 1 5 0a2.5 2.5 0 1 1 -5 0';
        iconPath += 'M19.5 9.5m-2.5 0a2.5 2.5 0 1 1 5 0a2.5 2.5 0 1 1 -5 0';
        iconPath += 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z';
      } else if (incidentType.includes('near-miss')) {
        iconPath =
          'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z';
      } else if (
        incidentType.includes('dangerous') ||
        incidentType.includes('speeding') ||
        incidentType.includes('reckless') ||
        incidentType.includes('road-rage')
      ) {
        iconPath =
          'M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z';
      } else if (incidentType.includes('fire')) {
        iconPath =
          'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zM12 11c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z';
      } else {
        // Default warning triangle for other incidents
        iconPath = 'M12 2L1 21h22L12 2zm0 3.17L19.83 19H4.17L12 5.17zM11 16h2v2h-2zm0-6h2v4h-2z';
        iconColor = '#ffffff';
      }
    } else {
      // Default warning triangle for unknown incidents
      iconPath = 'M12 2L1 21h22L12 2zm0 3.17L19.83 19H4.17L12 5.17zM11 16h2v2h-2zm0-6h2v4h-2z';
      iconColor = '#ffffff';
    }

    // Create a teardrop-shaped marker with a longer, more pointed end
    const svg = `
      <svg width="${baseSize}" height="${totalHeight}" viewBox="0 0 24 32" fill="none" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
            <feDropShadow dx="0" dy="2" stdDeviation="2" flood-color="rgba(0,0,0,0.3)"/>
          </filter>
        </defs>
        <!-- Teardrop shape with longer point -->
        <path d="M12 2C6.48 2 2 6.48 2 12c0 5.52 4.48 10 10 10s10-4.48 10-10C22 6.48 17.52 2 12 2zM12 20L8 28L12 26L16 28L12 20z" 
              fill="${color.fill}" 
              filter="url(#shadow)"/>
        <path d="M12 4C7.58 4 4 7.58 4 12c0 4.42 3.58 8 8 8s8-3.58 8-8C20 7.58 16.42 4 12 4zM12 18L10 24L12 22L14 24L12 18z" 
              fill="${color.stroke}"/>
        <!-- Icon in the center -->
        <path d="${iconPath}" fill="${iconColor}" transform="translate(0, -2) scale(0.8)"/>
      </svg>
    `;

    return {
      url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
      scaledSize: new window.google.maps.Size(baseSize, totalHeight),
      anchor: new window.google.maps.Point(anchorPoint, totalHeight),
    };
  };

  // Show loading state while config or Google Maps API is loading
  if (configLoading || !isLoaded) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">📍 Report Locations</h3>
        </div>
        <div className="h-96 flex items-center justify-center bg-gray-100">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
            <p className="text-sm text-gray-600">Loading map...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show error state if Google Maps failed to load
  if (loadError) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">📍 Report Locations</h3>
        </div>
        <div className="h-96 flex items-center justify-center bg-gray-100">
          <div className="text-center text-red-600">
            <p className="text-sm">Failed to load map: {loadError.message}</p>
          </div>
        </div>
      </div>
    );
  }

  if (reportsWithCoords.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">📍 Report Locations</h3>
        </div>
        <div className="h-96 flex items-center justify-center bg-gray-100">
          <p className="text-gray-600">
            No reports with location data available to display on map.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900">
          📍 Report Locations ({reportsWithCoords.length} reports)
        </h3>
        <p className="text-sm text-gray-600 mt-1">
          Click on a pin to select and view report details
        </p>
        <div className="flex items-center gap-4 mt-2 text-xs">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
            <span>Approved</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
            <span>Pending</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-red-500 rounded-full"></div>
            <span>Selected</span>
          </div>
        </div>
      </div>

      <div className="h-96">
        <GoogleMap
          key={`admin-map-${JSON.stringify(mapCenter)}-${mapZoom}`}
          mapContainerStyle={{ width: '100%', height: '100%' }}
          center={mapCenter}
          zoom={mapZoom}
          onLoad={onMapLoad}
          onUnmount={onMapUnmount}
          options={(() => {
            const config = getMurrayRoadMapConfig({
              center: mapCenter,
              zoom: mapZoom,
              restriction: mapRestrictions?.enabled
                ? {
                    latLngBounds: mapRestrictions.bounds,
                    strictBounds: mapRestrictions.strictBounds,
                  }
                : undefined,
              mapOptions: {
                disableDefaultUI: true,
                zoomControl: true,
              },
              mapStyles: [],
            });
            console.log('🔍 AdminReportsMap Options:', {
              restriction: config.options.restriction,
              mapRestrictions,
              enabled: mapRestrictions?.enabled,
              bounds: mapRestrictions?.bounds,
            });
            return config.options;
          })()}
        >
          {reportsWithCoords.map(report => (
            <Marker
              key={report.id}
              position={{
                lat: parseFloat(report.coordinates.lat),
                lng: parseFloat(report.coordinates.lng),
              }}
              icon={getMarkerIcon(report.incidentType, selectedReportId === report.id)}
              onClick={() => {
                console.log('🔍 AdminReportsMap: Marker clicked for report:', report.id);
                handleMarkerClick(report);
              }}
              title={`Report #${report.id} - ${report.incidentType} at ${report.location}`}
            />
          ))}
        </GoogleMap>
      </div>
    </div>
  );
}

function getSameSubmitterIpIds(report, ...lists) {
  if (!report?.submitterIp) return [];
  const ids = new Set();
  for (const list of lists) {
    for (const other of list || []) {
      if (other.submitterIp === report.submitterIp && other.id !== report.id) {
        ids.add(other.id);
      }
    }
  }
  return [...ids].sort((a, b) => b - a);
}

function SubmitterIpDetail({ report, otherIds, labelClass, valueClass }) {
  return (
    <div className="flex justify-between gap-4 items-start">
      <span className={labelClass}>Submitter IP:</span>
      <span className={`${valueClass} font-mono text-right`}>
        {report.submitterIp || 'Not recorded'}
        {otherIds.length > 0 && (
          <span className="block text-xs font-sans mt-1">
            Also on {otherIds.map(id => `SWARM-${id}`).join(', ')}
          </span>
        )}
      </span>
    </div>
  );
}

function AdminPanel({
  user,
  userList,
  setUserList,
  editingUser,
  userEditForm,
  setEditingUser,
  setUserEditForm,
  handleEditUser,
  handleDeleteUser,
  handleToggleAdmin,
  handleToggle2FA,
  exportReportsToCSV,
  importReportsFromSpreadsheet,
  saveAlertRecipients: _saveAlertRecipients,
  saveUserAlertPreferences,
  toggleUserAlertPreference,
  getUsersForAlertType: _getUsersForAlertType,
  showDemoAlert: _showDemoAlert,
  alertRecipients: _alertRecipients,
  userAlertPreferences,
  setUserAlertPreferences,
  setShowAlertModal: _setShowAlertModal,
  setAlertContent: _setAlertContent,
  setEmails: _setEmails,
  setPhones: _setPhones,
  emails: _emails,
  phones: _phones,
  setShowUserPanel: _setShowUserPanel,
  setCurrentPage,
  handleDeleteReport,
  handleApproveReport,
  handleRejectReport,
  handleRevokeApproval,
  handleRestoreReport,
  reports,
  pendingReports,
  setPendingReports,
  setIsAdminMode,
  setShowReportForm,
  showAdminPanel,
  setShowAdminPanel,
  setReports,
  onActiveAreaChange,
  activeArea,
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => {
    // Get initial tab from URL or default to 'reports'
    return searchParams.get('tab') || 'reports';
  });

  // Wrapper function to handle toggle with state update
  const handleToggleUserAlertPreference = async (userEmail, alertType) => {
    try {
      console.log('🔄 Handling toggle for:', { userEmail, alertType });
      await toggleUserAlertPreference(userEmail, alertType);

      // Update local state
      setUserAlertPreferences(prev => {
        const updated = { ...prev };
        if (!updated[userEmail]) {
          updated[userEmail] = {};
        }
        updated[userEmail] = {
          ...updated[userEmail],
          [alertType]: !updated[userEmail][alertType],
        };
        console.log('📧 Updated preferences:', updated);
        return updated;
      });
    } catch (error) {
      console.error('❌ Error toggling user alert preference:', error);
    }
  };

  // State for map-based report selection
  const [selectedReportFromMap, setSelectedReportFromMap] = useState(null);

  // Handle report selection from map
  const handleReportSelectFromMap = useCallback(report => {
    setSelectedReportFromMap(report);
    // Scroll to the report in the table
    const reportRow = document.getElementById(`report-row-${report.id}`);
    if (reportRow) {
      reportRow.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Highlight the row temporarily
      reportRow.classList.add('bg-blue-50', 'border-blue-200');
      setTimeout(() => {
        reportRow.classList.remove('bg-blue-50', 'border-blue-200');
      }, 3000);
    }
  }, []);

  // Handle tab changes and update URL
  const handleTabChange = tab => {
    setActiveTab(tab);
    // Update URL search params
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('tab', tab);
    setSearchParams(newSearchParams);

    // Ensure admin panel is shown when tab is set
    if (!showAdminPanel) {
      setShowAdminPanel(true);
    }
  };

  // Sync URL changes with active tab state
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl && tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
    }
  }, [searchParams, activeTab]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [confirmationModal, setConfirmationModal] = useState({
    show: false,
    title: '',
    message: '',
    onConfirm: null,
  });
  const [importPreview, setImportPreview] = useState([]);
  const [importHeaders, setImportHeaders] = useState([]);
  const [showConfirmImport, setShowConfirmImport] = useState(false);
  const [importStatus, setImportStatus] = useState({ loading: false, message: '', error: '' });
  const [importAsApproved, setImportAsApproved] = useState(false);
  const [showExportOptions, setShowExportOptions] = useState(false);

  // Password reset state
  const [passwordResetModal, setPasswordResetModal] = useState({
    show: false,
    user: null,
    newPassword: '',
    error: '',
    success: '',
  });

  // User edit form handlers
  const handleUserEditFormChange = e => {
    const { name, value, type, checked } = e.target;
    setUserEditForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const saveEditUser = e => {
    e.preventDefault();
    setUserList(prev =>
      prev.map(u => (u.email === editingUser.email ? { ...u, ...userEditForm } : u)),
    );
    setEditingUser(null);
  };

  const cancelEditUser = () => {
    setEditingUser(null);
  };

  // Password reset handlers
  const openPasswordResetModal = user => {
    setPasswordResetModal({
      show: true,
      user,
      newPassword: '',
      error: '',
      success: '',
    });
  };

  const closePasswordResetModal = () => {
    setPasswordResetModal({
      show: false,
      user: null,
      newPassword: '',
      error: '',
      success: '',
    });
  };

  const handlePasswordResetChange = e => {
    setPasswordResetModal(prev => ({
      ...prev,
      newPassword: e.target.value,
      error: '',
      success: '',
    }));
  };

  const handlePasswordResetSubmit = async e => {
    e.preventDefault();
    const { user, newPassword } = passwordResetModal;

    if (!newPassword || newPassword.length < 6) {
      setPasswordResetModal(prev => ({
        ...prev,
        error: 'Password must be at least 6 characters long',
      }));
      return;
    }

    try {
      await api.resetUserPassword(user.email, newPassword);
      setPasswordResetModal(prev => ({
        ...prev,
        success: 'Password has been reset successfully',
        newPassword: '',
      }));

      // Close modal after 2 seconds
      setTimeout(() => {
        closePasswordResetModal();
      }, 2000);
    } catch (error) {
      setPasswordResetModal(prev => ({
        ...prev,
        error: error.message || 'Failed to reset password',
      }));
    }
  };

  // Local wrapper functions for report approval that update editForm state
  const handleLocalApproveReport = async reportId => {
    try {
      await handleApproveReport(reportId);
      // Update the editForm to reflect the approval (reports are refreshed by handleApproveReport)
      setEditForm(prev => ({ ...prev, isApproved: true }));
      // Update editReport to reflect the change
      setEditReport(prev => (prev ? { ...prev, isApproved: true } : prev));
    } catch (error) {
      console.error('Failed to approve report:', error);
    }
  };

  const handleLocalRevokeApproval = async reportId => {
    try {
      await handleRevokeApproval(reportId);
      // Update the editForm to reflect the revoked approval
      setEditForm(prev => ({ ...prev, isApproved: false }));
      // Update editReport to reflect the change
      setEditReport(prev => (prev ? { ...prev, isApproved: false } : prev));
    } catch (error) {
      console.error('Failed to revoke approval:', error);
    }
  };

  const [adminCurrentPage, setAdminCurrentPage] = useState(1);
  const [reportSearchTerm, setReportSearchTerm] = useState('');
  const pageSize = 10;

  // Helper to check if a report is rejected (not approved but has been reviewed)
  const isReportRejected = report => !report.isApproved && report.approvedBy;

  // Filter reports based on search term, excluding rejected reports
  const filteredReports = (reports || []).filter(report => {
    // Exclude rejected reports from main list
    if (isReportRejected(report)) return false;
    if (!reportSearchTerm.trim()) return true;
    const searchLower = reportSearchTerm.toLowerCase();
    const referenceNumber = `swarm${report.id}`;
    return (
      report.reporterName?.toLowerCase().includes(searchLower) ||
      report.location?.toLowerCase().includes(searchLower) ||
      report.description?.toLowerCase().includes(searchLower) ||
      report.incidentType?.toLowerCase().includes(searchLower) ||
      report.estimatedIncidentTime?.toLowerCase().includes(searchLower) ||
      report.id?.toString().includes(searchLower) ||
      referenceNumber.includes(searchLower)
    );
  });

  // Archived (rejected) reports with search filtering
  const [archivedSearchTerm, setArchivedSearchTerm] = useState('');
  const [archivedCurrentPage, setArchivedCurrentPage] = useState(1);

  const archivedReports = (reports || []).filter(report => {
    // Only include rejected reports
    if (!isReportRejected(report)) return false;
    if (!archivedSearchTerm.trim()) return true;
    const searchLower = archivedSearchTerm.toLowerCase();
    const referenceNumber = `swarm${report.id}`;
    return (
      report.reporterName?.toLowerCase().includes(searchLower) ||
      report.location?.toLowerCase().includes(searchLower) ||
      report.description?.toLowerCase().includes(searchLower) ||
      report.incidentType?.toLowerCase().includes(searchLower) ||
      report.id?.toString().includes(searchLower) ||
      referenceNumber.includes(searchLower)
    );
  });

  const archivedTotalPages = Math.ceil((archivedReports.length || 0) / pageSize);
  const paginatedArchivedReports =
    archivedReports.slice((archivedCurrentPage - 1) * pageSize, archivedCurrentPage * pageSize) ||
    [];

  const totalPages = Math.ceil((filteredReports.length || 0) / pageSize);
  const paginatedReports =
    filteredReports.slice((adminCurrentPage - 1) * pageSize, adminCurrentPage * pageSize) || [];

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editReport, setEditReport] = useState(null);
  const [editReportIndex, setEditReportIndex] = useState(0); // Track current report index
  const [editForm, setEditForm] = useState({});
  const [editImages, setEditImages] = useState([]);
  const [editImageFiles, setEditImageFiles] = useState([]);

  // Track blob URLs for cleanup
  const blobUrlsRef = useRef(new Set());
  const errorSummaryRef = useRef(null);
  const [editLoading, setEditLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  // Cleanup blob URLs when component unmounts or modal closes
  const cleanupBlobUrls = useCallback(() => {
    blobUrlsRef.current.forEach(url => {
      if (url.startsWith('blob:')) {
        URL.revokeObjectURL(url);
      }
    });
    blobUrlsRef.current.clear();
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanupBlobUrls();
    };
  }, [cleanupBlobUrls]);

  const [imageEditorOpen, setImageEditorOpen] = useState(false);
  const [imageEditorIndex, setImageEditorIndex] = useState(null);
  const [imageEditorSrc, setImageEditorSrc] = useState(null);

  const [showMapPicker, setShowMapPicker] = useState(false);
  const viewPanelRef = useRef(null);

  const [editFormErrors, setEditFormErrors] = useState({});
  const [tableKey, setTableKey] = useState(0);

  // Keyboard navigation for edit modal
  useEffect(() => {
    const handleKeyDown = e => {
      if (!editModalOpen) return;

      if (e.key === 'ArrowLeft' && editReportIndex > 0) {
        e.preventDefault();
        navigateToPreviousReport();
      } else if (e.key === 'ArrowRight' && editReportIndex < reports.length - 1) {
        e.preventDefault();
        navigateToNextReport();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        closeEditModal();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [editModalOpen, editReportIndex, reports.length]);

  // Force table re-render when reports change
  useEffect(() => {
    setTableKey(prev => prev + 1);
  }, [reports]);

  const handleAllAlerts = userEmail => {
    const newPreferences = { ...userAlertPreferences };
    newPreferences[userEmail] = {
      all: true,
      reports: true,
      users: true,
      system: true,
      quick: true,
    };
    saveUserAlertPreferences(newPreferences);
  };

  const handleNoAlerts = userEmail => {
    const newPreferences = { ...userAlertPreferences };
    newPreferences[userEmail] = {
      all: false,
      reports: false,
      users: false,
      system: false,
      quick: false,
    };
    saveUserAlertPreferences(newPreferences);
  };

  const openEditModal = report => {
    console.log('🔍 openEditModal called with report:', report.id);

    // Find the latest version of the report from the reports array
    const latestReport = reports.find(r => r.id === report.id) || report;

    // Find the index of the report in the reports array
    const reportIndex = reports.findIndex(r => r.id === report.id);
    setEditReportIndex(reportIndex >= 0 ? reportIndex : 0);

    setEditReport(latestReport);

    // Convert estimatedIncidentTime for date input if timeUnknown is true
    const formData = { ...latestReport };
    if (formData.timeUnknown && formData.estimatedIncidentTime) {
      // Extract just the date part for date input
      formData.estimatedIncidentTime = formData.estimatedIncidentTime.split('T')[0];
    }

    // Convert date range fields for date inputs if isDateRange is true
    if (formData.isDateRange) {
      if (formData.incidentDateRangeStart) {
        formData.incidentDateRangeStart = formData.incidentDateRangeStart.split('T')[0];
      }
      if (formData.incidentDateRangeEnd) {
        formData.incidentDateRangeEnd = formData.incidentDateRangeEnd.split('T')[0];
      }
    }

    setEditForm(formData);

    // Handle both photoUrls (array) and photoUrl (single string) consistently
    let photos = [];
    if (latestReport.photoUrls && Array.isArray(latestReport.photoUrls)) {
      photos = latestReport.photoUrls;
    } else if (latestReport.photoUrl) {
      photos = [latestReport.photoUrl];
    }

    console.log('🔍 Setting editImages to:', photos);
    setEditImages(photos);
    setEditImageFiles([]);
    setEditModalOpen(true);

    // Force a small delay to ensure state is properly set
    setTimeout(() => {
      console.log('🔍 State after openEditModal:', {
        editImages: editImages?.length,
        editImageFiles: editImageFiles?.length,
        editReport: editReport?.id,
      });
    }, 100);
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditReport(null);
    setEditReportIndex(0);
    setEditForm({});
    setEditImages([]);
    setEditImageFiles([]);
    cleanupBlobUrls(); // Clean up all blob URLs
  };

  const navigateToNextReport = () => {
    if (editReportIndex < reports.length - 1) {
      const nextIndex = editReportIndex + 1;
      const nextReport = reports[nextIndex];
      setEditReportIndex(nextIndex);
      setEditReport(nextReport);
      // Ensure any open map picker resets for the new report
      setShowMapPicker(false);
      // Convert estimatedIncidentTime for date input if timeUnknown is true
      let estimatedIncidentTime = nextReport.estimatedIncidentTime || '';
      if (nextReport.timeUnknown && estimatedIncidentTime) {
        estimatedIncidentTime = estimatedIncidentTime.split('T')[0];
      }

      // Convert date range fields for date inputs if isDateRange is true
      let incidentDateRangeStart = nextReport.incidentDateRangeStart || '';
      let incidentDateRangeEnd = nextReport.incidentDateRangeEnd || '';
      if (nextReport.isDateRange) {
        if (incidentDateRangeStart) {
          incidentDateRangeStart = incidentDateRangeStart.split('T')[0];
        }
        if (incidentDateRangeEnd) {
          incidentDateRangeEnd = incidentDateRangeEnd.split('T')[0];
        }
      }

      setEditForm({
        location: nextReport.location || '',
        description: nextReport.description || '',
        incidentType: nextReport.incidentType || '',
        emergencyServicesAttended: nextReport.emergencyServicesAttended || '',
        policeAttended: nextReport.policeAttended || false,
        ambulanceAttended: nextReport.ambulanceAttended || false,
        fireBrigadeAttended: nextReport.fireBrigadeAttended || false,
        policeReportTime: nextReport.policeReportTime || '',
        policeOfficerName: nextReport.policeOfficerName || '',
        policeReportNumber: nextReport.policeReportNumber || '',
        reporterName: nextReport.reporterName || '',
        reporterNumber: nextReport.reporterNumber || '',
        reporterEmail: nextReport.reporterEmail || '',
        coordinates: nextReport.coordinates || '',
        photoCaptions: nextReport.photoCaptions || [],
        estimatedIncidentTime,
        timeUnknown: nextReport.timeUnknown || false,
        incidentDateRangeStart,
        incidentDateRangeEnd,
        isDateRange: nextReport.isDateRange || false,
        witnessedIncident: nextReport.witnessedIncident || '',
        evidenceDescription: nextReport.evidenceDescription || '',
        injuries: nextReport.injuries || '',
        injuryCount: nextReport.injuryCount || '',
        injuryNotes: nextReport.injuryNotes || '',
        isApproved: nextReport.isApproved || false,
        approvedBy: nextReport.approvedBy || '',
        approvedAt: nextReport.approvedAt || '',
        createdAt: nextReport.createdAt || '',
        updatedAt: nextReport.updatedAt || '',
        id: nextReport.id || '',
        userEmail: nextReport.userEmail || '',
        photoUrls: nextReport.photoUrls || [],
        photoApprovalStatus: nextReport.photoApprovalStatus || '',
        success: '', // Clear any success message
        error: '', // Clear any error message
      });
      // Handle both photoUrls (array) and photoUrl (single string) consistently
      let photos = [];
      if (nextReport.photoUrls && Array.isArray(nextReport.photoUrls)) {
        photos = nextReport.photoUrls;
      } else if (nextReport.photoUrl) {
        photos = [nextReport.photoUrl];
      }
      setEditImages(photos);
      setEditImageFiles([]);
      setEditFormErrors({});
    }
  };

  const navigateToPreviousReport = () => {
    if (editReportIndex > 0) {
      const prevIndex = editReportIndex - 1;
      const prevReport = reports[prevIndex];
      setEditReportIndex(prevIndex);
      setEditReport(prevReport);
      // Ensure any open map picker resets for the new report
      setShowMapPicker(false);
      // Convert estimatedIncidentTime for date input if timeUnknown is true
      let estimatedIncidentTime = prevReport.estimatedIncidentTime || '';
      if (prevReport.timeUnknown && estimatedIncidentTime) {
        estimatedIncidentTime = estimatedIncidentTime.split('T')[0];
      }

      // Convert date range fields for date inputs if isDateRange is true
      let incidentDateRangeStart = prevReport.incidentDateRangeStart || '';
      let incidentDateRangeEnd = prevReport.incidentDateRangeEnd || '';
      if (prevReport.isDateRange) {
        if (incidentDateRangeStart) {
          incidentDateRangeStart = incidentDateRangeStart.split('T')[0];
        }
        if (incidentDateRangeEnd) {
          incidentDateRangeEnd = incidentDateRangeEnd.split('T')[0];
        }
      }

      setEditForm({
        location: prevReport.location || '',
        description: prevReport.description || '',
        incidentType: prevReport.incidentType || '',
        emergencyServicesAttended: prevReport.emergencyServicesAttended || '',
        policeAttended: prevReport.policeAttended || false,
        ambulanceAttended: prevReport.ambulanceAttended || false,
        fireBrigadeAttended: prevReport.fireBrigadeAttended || false,
        policeReportTime: prevReport.policeReportTime || '',
        policeOfficerName: prevReport.policeOfficerName || '',
        policeReportNumber: prevReport.policeReportNumber || '',
        reporterName: prevReport.reporterName || '',
        reporterNumber: prevReport.reporterNumber || '',
        reporterEmail: prevReport.reporterEmail || '',
        coordinates: prevReport.coordinates || '',
        photoCaptions: prevReport.photoCaptions || [],
        estimatedIncidentTime,
        timeUnknown: prevReport.timeUnknown || false,
        incidentDateRangeStart,
        incidentDateRangeEnd,
        isDateRange: prevReport.isDateRange || false,
        witnessedIncident: prevReport.witnessedIncident || '',
        evidenceDescription: prevReport.evidenceDescription || '',
        injuries: prevReport.injuries || '',
        injuryCount: prevReport.injuryCount || '',
        injuryNotes: prevReport.injuryNotes || '',
        isApproved: prevReport.isApproved || false,
        approvedBy: prevReport.approvedBy || '',
        approvedAt: prevReport.approvedAt || '',
        createdAt: prevReport.createdAt || '',
        updatedAt: prevReport.updatedAt || '',
        id: prevReport.id || '',
        userEmail: prevReport.userEmail || '',
        photoUrls: prevReport.photoUrls || [],
        photoApprovalStatus: prevReport.photoApprovalStatus || '',
        success: '', // Clear any success message
        error: '', // Clear any error message
      });
      // Handle both photoUrls (array) and photoUrl (single string) consistently
      let photos = [];
      if (prevReport.photoUrls && Array.isArray(prevReport.photoUrls)) {
        photos = prevReport.photoUrls;
      } else if (prevReport.photoUrl) {
        photos = [prevReport.photoUrl];
      }
      setEditImages(photos);
      setEditImageFiles([]);
      setEditFormErrors({});
    }
  };

  const handleEditFormChange = e => {
    const { name, value, type, checked } = e.target;

    if (name === 'timeUnknown') {
      const isTimeUnknown = checked;
      setEditForm(prev => ({
        ...prev,
        timeUnknown: isTimeUnknown,
        // If switching to time unknown, clear the time portion
        estimatedIncidentTime:
          isTimeUnknown && prev.estimatedIncidentTime
            ? prev.estimatedIncidentTime.split('T')[0]
            : prev.estimatedIncidentTime,
      }));
    } else if (name === 'isDateRange') {
      const isDateRange = checked;
      setEditForm(prev => ({
        ...prev,
        isDateRange,
        timeUnknown: isDateRange ? true : prev.timeUnknown, // Date ranges are always time unknown
        // Clear range fields if unchecking
        incidentDateRangeStart: isDateRange ? prev.incidentDateRangeStart : '',
        incidentDateRangeEnd: isDateRange ? prev.incidentDateRangeEnd : '',
      }));
    } else {
      setEditForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    }
  };

  const handleRemoveImage = idx => {
    setEditImages(prev => (Array.isArray(prev) ? prev.filter((_, i) => i !== idx) : []));
    setEditImageFiles(prev => (Array.isArray(prev) ? prev.filter((_, i) => i !== idx) : []));
    // Also remove the corresponding photo caption if present
    setEditForm(prev => {
      const captions = Array.isArray(prev.photoCaptions) ? [...prev.photoCaptions] : [];
      captions.splice(idx, 1);
      return { ...prev, photoCaptions: captions };
    });
  };

  const handleEditSave = async (navigateToNext = true) => {
    // Use shared validation utility
    const { errors } = validateForm(editForm, true); // isAdminMode=true
    setEditFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      // Scroll to error summary
      if (errorSummaryRef.current) {
        errorSummaryRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
        // Focus on error summary for screen readers
        setTimeout(() => {
          errorSummaryRef.current.focus();
        }, 500);
      }
      return;
    }
    setEditLoading(true);
    setEditForm(prev => ({ ...prev, error: '' }));
    try {
      let photoUrls = [];

      console.log('🔍 AdminPanel Save - editImages:', editImages);
      console.log('🔍 AdminPanel Save - editImageFiles:', editImageFiles);

      // Detailed structure logging
      console.log(
        '🔍 AdminPanel Save - editImages structure:',
        editImages.map((img, idx) => ({
          index: idx,
          value: img,
          isBlob: img && img.startsWith('blob:'),
          isData: img && img.startsWith('data:'),
          isExisting: img && !img.startsWith('blob:') && !img.startsWith('data:'),
        })),
      );

      console.log(
        '🔍 AdminPanel Save - editImageFiles structure:',
        editImageFiles.map((file, idx) => ({
          index: idx,
          hasFile: !!file,
          fileName: file?.name,
          fileSize: file?.size,
        })),
      );

      // Build the final photo array by processing each position
      // First, handle existing images (non-blob URLs)
      const existingImages = editImages.filter(
        img => img && !img.startsWith('blob:') && !img.startsWith('data:'),
      );
      photoUrls = [...existingImages];

      // Then, handle all files that need to be uploaded (both edited existing images and new uploads)
      const filesToUpload = editImageFiles.filter(file => file !== null && file !== undefined);

      // Add placeholders for all files that need to be uploaded
      filesToUpload.forEach(() => {
        photoUrls.push('UPLOAD_PLACEHOLDER');
      });

      // Upload files and replace placeholders
      if (filesToUpload.length > 0) {
        console.log('🔍 AdminPanel Save - Uploading files:', filesToUpload.length);
        setIsUploading(true);
        setUploadProgress(0);

        const uploadResponse = await api.uploadPhotos(filesToUpload, progress => {
          setUploadProgress(progress);
        });

        setIsUploading(false);
        setUploadProgress(0);

        if (uploadResponse.files) {
          const newPhotoUrls = uploadResponse.files.map(f => f.filename);

          // Replace placeholders with actual URLs
          let urlIndex = 0;
          for (let i = 0; i < photoUrls.length; i++) {
            if (photoUrls[i] === 'UPLOAD_PLACEHOLDER') {
              photoUrls[i] = newPhotoUrls[urlIndex];
              urlIndex++;
            }
          }

          console.log('🔍 AdminPanel Save - New photo URLs:', newPhotoUrls);
        }
      }

      console.log('🔍 AdminPanel Save - Final photoUrls:', photoUrls);
      let photoCaptions = parseJsonArray(editForm.photoCaptions);

      // Ensure captions array matches the total number of photos (existing + new)
      const totalPhotos = photoUrls.length;
      if (photoCaptions.length < totalPhotos) {
        // Add empty captions for new photos
        photoCaptions = [...photoCaptions, ...Array(totalPhotos - photoCaptions.length).fill('')];
      } else if (photoCaptions.length > totalPhotos) {
        // Remove excess captions if photos were removed
        photoCaptions = photoCaptions.slice(0, totalPhotos);
      }
      const updateData = {
        ...editForm,
        coordinates:
          typeof editForm.coordinates === 'string'
            ? editForm.coordinates
            : editForm.coordinates &&
                typeof editForm.coordinates.lat === 'number' &&
                typeof editForm.coordinates.lng === 'number'
              ? `${editForm.coordinates.lat}, ${editForm.coordinates.lng}`
              : '',
        injuryCount:
          editForm.injuryCount === '' ||
          editForm.injuryCount === undefined ||
          editForm.injuryCount === null
            ? null
            : Number(editForm.injuryCount),
        photoUrls,
        photoCaptions,
      };
      await api.updateReport(editReport.id, updateData);
      setEditLoading(false);

      // Update both reports and pendingReports so all tables/lists reflect the change
      if (typeof setReports === 'function') {
        setReports(prevReports => {
          const updatedReports = prevReports.map(r =>
            r.id === editReport.id
              ? { ...r, ...updateData, updatedAt: new Date().toISOString() }
              : r,
          );
          return updatedReports;
        });
      }

      if (typeof setPendingReports === 'function') {
        setPendingReports(prevPending => {
          const updatedPending = prevPending.map(r =>
            r.id === editReport.id
              ? { ...r, ...updateData, updatedAt: new Date().toISOString() }
              : r,
          );
          return updatedPending;
        });
      }

      // Force a re-render by updating the selected report if it's the one being edited
      if (selectedReport === editReport.id) {
        setSelectedReport(null);
        setTimeout(() => setSelectedReport(editReport.id), 100);
      }

      // Check if there are more reports to edit and navigation is requested
      if (navigateToNext && editReportIndex < reports.length - 1) {
        // Move to next report automatically
        navigateToNextReport();
        // Show success message for moving to next report
        setEditForm(prev => ({
          ...prev,
          success: `Report saved successfully! Moving to report ${editReportIndex + 2} of ${reports.length}`,
          error: '',
        }));
        // Clear success message after 3 seconds
        setTimeout(() => {
          setEditForm(prev => ({ ...prev, success: '' }));
        }, 3000);
      } else if (navigateToNext && editReportIndex >= reports.length - 1) {
        // This was the last report, show final success message before closing
        setEditForm(prev => ({
          ...prev,
          success: 'Report saved successfully! This was the last report to edit.',
          error: '',
        }));
        // Close modal after 2 seconds
        setTimeout(() => {
          closeEditModal();
        }, 2000);
      } else {
        // Stay on current report, show save success message
        setEditForm(prev => ({
          ...prev,
          success: 'Report saved successfully!',
          error: '',
        }));
        // Clear success message after 3 seconds
        setTimeout(() => {
          setEditForm(prev => ({ ...prev, success: '' }));
        }, 3000);
      }
    } catch (error) {
      setEditLoading(false);
      setIsUploading(false);
      setUploadProgress(0);
      setEditForm(prev => ({ ...prev, error: `Failed to update report: ${error.message}` }));
    }
  };

  const handleImportFileChange = e => {
    const file = e.target.files[0];

    // Debug: Log file information
    console.log('File selected:', {
      name: file?.name,
      type: file?.type,
      size: file?.size,
    });

    // Check if file is a CSV - be more flexible with MIME type checking
    const isValidCSV =
      file &&
      (file.type === 'text/csv' ||
        file.type === 'application/csv' ||
        file.type === 'text/plain' ||
        file.name.toLowerCase().endsWith('.csv'));

    if (isValidCSV) {
      setImportFile(file);
      setImportStatus({ loading: false, message: '', error: '' });
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: results => {
          setImportHeaders(results.meta.fields);
          setImportPreview(results.data);
        },
      });
    } else {
      console.error('Invalid CSV file:', {
        name: file?.name,
        type: file?.type,
        expectedTypes: ['text/csv', 'application/csv', 'text/plain', '*.csv'],
      });
      alert('Please select a valid CSV file.');
    }
  };

  const handleImportSubmit = async e => {
    e.preventDefault();
    if (!importFile) {
      setImportStatus({ loading: false, message: '', error: 'Please select a file to import' });
      return;
    }
    setShowConfirmImport(true);
  };

  const handleConfirmImport = async () => {
    setImportStatus({ loading: true, message: '', error: '' });
    try {
      await importReportsFromSpreadsheet(importFile, importAsApproved);

      // Refresh the reports list to show newly imported data
      try {
        const reportsResponse = await api.getReports();
        setReports(reportsResponse.reports || []);
        console.log('✅ Reports refreshed after import');
      } catch (refreshError) {
        console.error('Failed to refresh reports after import:', refreshError);
      }

      setShowImportModal(false);
      setImportFile(null);
      setImportPreview([]);
      setImportHeaders([]);
      setShowConfirmImport(false);
      setImportStatus({ loading: false, message: 'Reports imported successfully!', error: '' });
    } catch (error) {
      setImportStatus({ loading: false, message: '', error: error.message });
    }
  };

  const handleImportCancel = () => {
    setShowImportModal(false);
    setImportFile(null);
    setImportPreview([]);
    setImportHeaders([]);
    setShowConfirmImport(false);
    setImportStatus({ loading: false, message: '', error: '' });
  };

  const showConfirmationModal = (title, message, onConfirm) => {
    setConfirmationModal({ show: true, title, message, onConfirm });
  };

  const closeConfirmationModal = () => {
    setConfirmationModal({ show: false, title: '', message: '', onConfirm: null });
  };

  const handleConfirmAction = () => {
    if (confirmationModal.onConfirm) {
      confirmationModal.onConfirm();
    }
    closeConfirmationModal();
  };

  // Helper function to format incident type for display
  const formatIncidentType = incidentType => {
    if (!incidentType) return '';

    const typeMap = {
      // Motor Vehicles
      'car-collision': 'Car Collision (Car vs Car)',
      'multiple-vehicle-incident': 'Multiple Vehicle Incident',
      'car-pedestrian': 'Car vs Pedestrian',
      'car-bicycle': 'Car vs Bicycle',
      'car-motorcycle': 'Car vs Motorcycle',
      'car-animal': 'Car vs Animal',
      'car-property': 'Car vs Property',
      'car-rollover': 'Car Rollover',
      'car-fire': 'Car Fire',

      // Motorcycles
      'motorcycle-collision': 'Motorcycle Collision',
      'motorcycle-car': 'Motorcycle vs Car',
      'motorcycle-pedestrian': 'Motorcycle vs Pedestrian',
      'motorcycle-bicycle': 'Motorcycle vs Bicycle',
      'motorcycle-animal': 'Motorcycle vs Animal',
      'motorcycle-property': 'Motorcycle vs Property',
      'motorcycle-single': 'Single Motorcycle Accident',

      // Trucks
      'truck-collision': 'Truck Collision',
      'truck-car': 'Truck vs Car',
      'truck-motorcycle': 'Truck vs Motorcycle',
      'truck-bicycle': 'Truck vs Bicycle',
      'truck-pedestrian': 'Truck vs Pedestrian',
      'truck-animal': 'Truck vs Animal',
      'truck-property': 'Truck vs Property',
      'truck-rollover': 'Truck Rollover',
      'truck-load-spill': 'Truck Load Spill',

      // Bicycles
      'bicycle-collision': 'Bicycle Collision',
      'bicycle-car': 'Bicycle vs Car',
      'bicycle-motorcycle': 'Bicycle vs Motorcycle',
      'bicycle-truck': 'Bicycle vs Truck',
      'bicycle-pedestrian': 'Bicycle vs Pedestrian',
      'bicycle-animal': 'Bicycle vs Animal',
      'bicycle-property': 'Bicycle vs Property',
      'bicycle-single': 'Single Bicycle Accident',

      // Pedestrians
      'pedestrian-car': 'Pedestrian vs Car',
      'pedestrian-motorcycle': 'Pedestrian vs Motorcycle',
      'pedestrian-truck': 'Pedestrian vs Truck',
      'pedestrian-bicycle': 'Pedestrian vs Bicycle',
      'pedestrian-animal': 'Pedestrian vs Animal',
      'pedestrian-fall': 'Pedestrian Fall/Slip',
      'pedestrian-property': 'Pedestrian vs Property',

      // Animals
      'animal-car': 'Animal vs Car',
      'animal-motorcycle': 'Animal vs Motorcycle',
      'animal-truck': 'Animal vs Truck',
      'animal-bicycle': 'Animal vs Bicycle',
      'animal-pedestrian': 'Animal vs Pedestrian',
      'animal-loose': 'Loose Animal',

      // Near Misses & Dangerous Driving
      'near-miss-car': 'Near Miss - Car',
      'near-miss-motorcycle': 'Near Miss - Motorcycle',
      'near-miss-truck': 'Near Miss - Truck',
      'near-miss-bicycle': 'Near Miss - Bicycle',
      'near-miss-pedestrian': 'Near Miss - Pedestrian',
      'dangerous-driving-car': 'Dangerous Driving - Car',
      'dangerous-driving-motorcycle': 'Dangerous Driving - Motorcycle',
      'dangerous-driving-truck': 'Dangerous Driving - Truck',
      'dangerous-driving-bicycle': 'Dangerous Driving - Bicycle',
      'road-rage': 'Road Rage Incident',
      speeding: 'Excessive Speeding',
      'reckless-driving': 'Reckless Driving',

      // Road & Traffic Hazards
      'traffic-light-malfunction': 'Traffic-light Malfunction',
      'traffic-light-damage': 'Traffic-light Damage',
      'road-hazard': 'Road Hazard',
      'debris-on-road': 'Debris on Road',
      'pothole-or-surface-damage': 'Pothole or Surface Damage',
      'signage-issue': 'Signage Issue',
      'roadwork-issue': 'Roadwork Issue',
      'traffic-congestion-or-gridlock': 'Traffic Congestion/Gridlock',
      'other-traffic-hazard': 'Other Traffic Hazard',

      // Other
      'property-damage': 'Property Damage Only',
      fire: 'Fire Incident',
      flooding: 'Flooding/Water Damage',
      debris: 'Debris on Road',
      'traffic-light': 'Traffic Light Malfunction (Legacy)',
      other: 'Other/Unspecified',
    };

    return typeMap[incidentType] || incidentType;
  };

  // Helper function to format time for display - Always use Melbourne timezone
  const formatTimeForDisplay = (
    timeString,
    timeUnknown = false,
    isDateRange = false,
    rangeStart = null,
    rangeEnd = null,
  ) => {
    // Handle date ranges first
    if (isDateRange === true || isDateRange === 'true') {
      if (rangeStart && rangeEnd) {
        try {
          const startDate = new Date(rangeStart);
          const endDate = new Date(rangeEnd);
          if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
            const options = {
              day: 'numeric',
              month: 'short',
              year: 'numeric',
              timeZone: 'Australia/Melbourne',
            };
            return `Between ${startDate.toLocaleDateString('en-AU', options)} - ${endDate.toLocaleDateString('en-AU', options)}`;
          }
        } catch {
          // Fall through to default handling
        }
      }
      return 'Date range (unknown)';
    }

    if (!timeString) return '—';

    try {
      const date = new Date(timeString);
      if (isNaN(date.getTime())) return timeString;

      // If time is unknown, show date-only format with tilde prefix
      if (timeUnknown === true || timeUnknown === 'true') {
        const options = {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
          timeZone: 'Australia/Melbourne',
        };
        return `~${date.toLocaleDateString('en-AU', options)}`;
      }

      // Format as "12 Jul 2025, 2:30 PM" in Melbourne timezone
      const options = {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZone: 'Australia/Melbourne',
      };

      return date.toLocaleDateString('en-AU', options);
    } catch (error) {
      return timeString;
    }
  };

  // Helper function to safely get file URL (handles both old and new formats)
  const getSafeFileUrl = photoUrl => {
    if (!photoUrl) return null;

    // If it's already a full URL, return as is
    if (photoUrl.startsWith('http://') || photoUrl.startsWith('https://')) {
      return photoUrl;
    }

    // If it starts with /uploads/, it's the old format, extract filename
    if (photoUrl.startsWith('/uploads/')) {
      const filename = photoUrl.replace('/uploads/', '');
      const url = api.getFileUrl(filename);
      return url;
    }

    // Otherwise, assume it's just a filename
    const url = api.getFileUrl(photoUrl);
    return url;
  };

  // Helper function to generate incident type icon as SVG data URL for placeholders
  const getIncidentTypeIconUrl = (incidentType, size = 80) => {
    // Define colors for different incident categories
    const colors = {
      // Motor Vehicles - Red
      car: { fill: '#ef4444', stroke: '#dc2626' },
      // Motorcycles - Orange
      motorcycle: { fill: '#f97316', stroke: '#ea580c' },
      // Scooters - Teal
      scooter: { fill: '#14b8a6', stroke: '#0d9488' },
      // Trucks - Purple
      truck: { fill: '#8b5cf6', stroke: '#7c3aed' },
      // Bicycles - Blue
      bicycle: { fill: '#3b82f6', stroke: '#2563eb' },
      // Pedestrians - Green
      pedestrian: { fill: '#10b981', stroke: '#059669' },
      // Animals - Brown
      animal: { fill: '#a16207', stroke: '#92400e' },
      // Near Misses - Yellow
      nearMiss: { fill: '#eab308', stroke: '#ca8a04' },
      // Dangerous Driving - Dark Red
      dangerous: { fill: '#dc2626', stroke: '#b91c1c' },
      // Other - Gray
      other: { fill: '#6b7280', stroke: '#4b5563' },
    };

    // Determine color based on incident type
    let color = colors.other;
    if (incidentType) {
      // Prioritize bicycle and pedestrian colors for safety awareness
      if (incidentType.includes('bicycle')) {
        color = colors.bicycle;
      } else if (incidentType.includes('pedestrian')) {
        color = colors.pedestrian;
      } else if (incidentType.includes('car') || incidentType.includes('vehicle')) {
        color = colors.car;
      } else if (incidentType.includes('motorcycle')) {
        color = colors.motorcycle;
      } else if (incidentType.includes('scooter')) {
        color = colors.scooter;
      } else if (incidentType.includes('truck')) {
        color = colors.truck;
      } else if (incidentType.includes('animal')) {
        color = colors.animal;
      } else if (incidentType.includes('near-miss')) {
        color = colors.nearMiss;
      } else if (
        incidentType.includes('dangerous') ||
        incidentType.includes('speeding') ||
        incidentType.includes('reckless') ||
        incidentType.includes('road-rage')
      ) {
        color = colors.dangerous;
      }
    }

    // Generate SVG with appropriate icon based on incident type
    let iconPath = '';
    let iconColor = '#ffffff';

    if (incidentType) {
      // Prioritize bicycle and pedestrian icons for safety awareness
      if (incidentType.includes('bicycle')) {
        iconPath =
          'M15.5 5.5c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zM5 12c-2.8 0-5 2.2-5 5s2.2 5 5 5 5-2.2 5-5-2.2-5-5-5zm0 8.5c-1.9 0-3.5-1.6-3.5-3.5s1.6-3.5 3.5-3.5 3.5 1.6 3.5 3.5-1.6 3.5-3.5 3.5zm5.8-10l2.4-2.4.8.8c1.3 1.3 3 2.1 5.1 2.1V9c-1.5 0-2.7-.6-3.6-1.5l-1.9-1.9c-.5-.4-1-.6-1.6-.6s-1.1.2-1.4.6L7.8 8.4c-.4.4-.6.9-.6 1.4 0 .6.2 1.1.6 1.4L11 14v5h2v-6.2l-2.2-2.3zM19 12c-2.8 0-5 2.2-5 5s2.2 5 5 5 5-2.2 5-5-2.2-5-5-5zm0 8.5c-1.9 0-3.5-1.6-3.5-3.5s1.6-3.5 3.5-3.5 3.5 1.6 3.5 3.5-1.6 3.5-3.5 3.5z';
      } else if (incidentType.includes('pedestrian')) {
        iconPath =
          'M12 2c1.1 0 2 .9 2 2s-.9 2-2 2-2-.9-2-2 .9-2 2-2zm9 7h-6v13h-2v-6h-2v6H9V9H3V7h18v2z';
      } else if (incidentType.includes('car') || incidentType.includes('vehicle')) {
        iconPath =
          'M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.22.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z';
      } else if (incidentType.includes('motorcycle')) {
        iconPath =
          'M19.44 9.03L15.41 5H11v2h3.59l2 2H5c-2.8 0-5 2.2-5 5s2.2 5 5 5c2.46 0 4.45-1.69 4.9-4h.65c.45 2.31 2.44 4 4.9 4 2.8 0 5-2.2 5-5 0-2.65-2.15-4.8-4.56-4.97zM7.82 15C7.4 16.15 6.28 17 5 17c-1.66 0-3-1.34-3-3s1.34-3 3-3c1.28 0 2.4.85 2.82 2H7.82zM19 17c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3z';
      } else if (incidentType.includes('scooter')) {
        iconPath =
          'M12 2C8.13 2 5 5.13 5 9c0 1.74.69 3.32 1.81 4.48L7 15h10l.19-1.52C18.31 12.32 19 10.74 19 9c0-3.87-3.13-7-7-7zM8.5 9c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5-.67 1.5-1.5 1.5-1.5-.67-1.5-1.5zm7 0c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5-.67 1.5-1.5 1.5-1.5-.67-1.5-1.5zM12 17c-1.1 0-2-.9-2-2h4c0 1.1-.9 2-2 2z';
      } else if (incidentType.includes('truck')) {
        iconPath =
          'M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h4c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm13.5-9l1.96 2.5H17V9.5h2.5zm-1.5 9c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z';
      } else if (incidentType.includes('animal')) {
        iconPath = 'M4.5 9.5m-2.5 0a2.5 2.5 0 1 1 5 0a2.5 2.5 0 1 1 -5 0';
        iconPath += 'M19.5 9.5m-2.5 0a2.5 2.5 0 1 1 5 0a2.5 2.5 0 1 1 -5 0';
        iconPath += 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z';
      } else if (incidentType.includes('near-miss')) {
        iconPath =
          'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z';
      } else if (
        incidentType.includes('dangerous') ||
        incidentType.includes('speeding') ||
        incidentType.includes('reckless') ||
        incidentType.includes('road-rage')
      ) {
        iconPath =
          'M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z';
      } else if (incidentType.includes('fire')) {
        iconPath =
          'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zM12 11c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z';
      } else {
        // Default warning triangle for other incidents
        iconPath = 'M12 2L1 21h22L12 2zm0 3.17L19.83 19H4.17L12 5.17zM11 16h2v2h-2zm0-6h2v4h-2z';
        iconColor = '#ffffff';
      }
    } else {
      // Default warning triangle for unknown incidents
      iconPath = 'M12 2L1 21h22L12 2zm0 3.17L19.83 19H4.17L12 5.17zM11 16h2v2h-2zm0-6h2v4h-2z';
      iconColor = '#ffffff';
    }

    // Create SVG with background circle and icon
    const svg = `
      <svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" fill="none" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
            <feDropShadow dx="0" dy="2" stdDeviation="2" flood-color="rgba(0,0,0,0.2)"/>
          </filter>
        </defs>
        <!-- Background circle -->
        <circle cx="${size / 2}" cy="${size / 2}" r="${size / 2 - 4}" fill="${color.fill}" stroke="${color.stroke}" stroke-width="2" filter="url(#shadow)"/>
        <!-- Icon -->
        <path d="${iconPath}" fill="${iconColor}" transform="translate(${size / 2 - 12}, ${size / 2 - 12}) scale(1.5)"/>
      </svg>
    `;

    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
  };

  // State for photo preview modal
  const [previewModal, setPreviewModal] = useState({
    open: false,
    url: '',
    caption: '',
    photos: [],
    currentIndex: 0,
  });

  const openPreviewModal = (url, caption, photos = [], currentIndex = 0) => {
    setPreviewModal({
      open: true,
      url,
      caption,
      photos: photos.length > 0 ? photos : [{ url, caption }],
      currentIndex,
    });
  };

  const closePreviewModal = () =>
    setPreviewModal({
      open: false,
      url: '',
      caption: '',
      photos: [],
      currentIndex: 0,
    });

  const nextPhoto = useCallback(() => {
    if (previewModal.photos.length > 1) {
      const nextIndex = (previewModal.currentIndex + 1) % previewModal.photos.length;
      setPreviewModal(prev => ({
        ...prev,
        currentIndex: nextIndex,
        url: prev.photos[nextIndex].url,
        caption: prev.photos[nextIndex].caption,
      }));
    }
  }, [previewModal.photos.length, previewModal.currentIndex]);

  const prevPhoto = useCallback(() => {
    if (previewModal.photos.length > 1) {
      const prevIndex =
        previewModal.currentIndex === 0
          ? previewModal.photos.length - 1
          : previewModal.currentIndex - 1;
      setPreviewModal(prev => ({
        ...prev,
        currentIndex: prevIndex,
        url: prev.photos[prevIndex].url,
        caption: prev.photos[prevIndex].caption,
      }));
    }
  }, [previewModal.photos.length, previewModal.currentIndex]);

  // Keyboard navigation for gallery
  useEffect(() => {
    const handleKeyDown = e => {
      if (!previewModal.open) return;

      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          prevPhoto();
          break;
        case 'ArrowRight':
          e.preventDefault();
          nextPhoto();
          break;
        case 'Escape':
          e.preventDefault();
          closePreviewModal();
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [
    previewModal.open,
    previewModal.photos.length,
    previewModal.currentIndex,
    nextPhoto,
    prevPhoto,
  ]);

  // Photo approval handlers
  const handleApprovePhoto = async (reportId, photoIndex) => {
    try {
      await api.approvePhoto(reportId, photoIndex);
      // Refresh the pending reports to show updated status
      const response = await api.getPendingReports();
      setPendingReports(response.reports);
    } catch (error) {
      console.error('Failed to approve photo:', error);
      alert(`Failed to approve photo: ${error.message}`);
    }
  };

  const handleRejectPhoto = async (reportId, photoIndex) => {
    try {
      await api.rejectPhoto(reportId, photoIndex);
      // Refresh the pending reports to show updated status
      const response = await api.getPendingReports();
      setPendingReports(response.reports);
    } catch (error) {
      console.error('Failed to reject photo:', error);
      alert(`Failed to reject photo: ${error.message}`);
    }
  };

  // Open image editor for a given image index
  const openImageEditor = async (idx, type = 'auto') => {
    let src = '';

    console.log('🔍 openImageEditor called with:', {
      idx,
      type,
      editImages: editImages?.length,
      editImageFiles: editImageFiles?.length,
    });
    console.log('🔍 editImages array:', editImages);
    console.log('🔍 editImageFiles array:', editImageFiles);

    if (type === 'file') {
      // Explicitly editing a file
      if (editImageFiles && editImageFiles[idx]) {
        src = URL.createObjectURL(editImageFiles[idx]);
        blobUrlsRef.current.add(src); // Track for cleanup
      } else {
        console.error('❌ No file found at index', idx, 'in editImageFiles');
        alert('Error: No image file found to edit. Please try again.');
        return;
      }
    } else if (type === 'existing') {
      // Explicitly editing an existing image
      if (Array.isArray(editImages) && editImages[idx]) {
        src = api.getFileUrl(editImages[idx]);
      } else {
        console.error('❌ No existing image found at index', idx, 'in editImages');
        alert('Error: No existing image found to edit. Please try again.');
        return;
      }
    } else {
      // Auto-detect (original logic)
      if (editImageFiles && editImageFiles[idx]) {
        src = URL.createObjectURL(editImageFiles[idx]);
        blobUrlsRef.current.add(src); // Track for cleanup
      } else if (Array.isArray(editImages) && editImages[idx]) {
        src = api.getFileUrl(editImages[idx]);
      } else {
        console.error('❌ No image found at index', idx, 'in either editImages or editImageFiles');
        alert('Error: No image found to edit. Please try again.');
        return;
      }
    }

    if (!src) {
      console.error('❌ Failed to generate image source for editing');
      alert('Error: Could not load image for editing. Please try again.');
      return;
    }

    console.log('✅ Setting image editor with src:', src);
    setImageEditorSrc(src);
    setImageEditorIndex(idx);
    setImageEditorOpen(true);
  };

  // Handle save from image editor
  const handleImageEditorSave = file => {
    // Check if we're editing an existing image or a new file
    if (editImageFiles[imageEditorIndex]) {
      // Editing a new file - update editImageFiles
      setEditImageFiles(prev => {
        const files = [...prev];
        files[imageEditorIndex] = file;
        return files;
      });
    } else if (Array.isArray(editImages) && editImages[imageEditorIndex]) {
      // Editing an existing image - add to editImageFiles and remove from editImages
      setEditImageFiles(prev => {
        const files = [...prev];
        files[imageEditorIndex] = file;
        return files;
      });

      // Remove the original image from editImages since we now have an edited version
      setEditImages(prev => {
        const images = [...prev];
        images[imageEditorIndex] = null; // Mark as removed
        return images;
      });
    }

    setImageEditorOpen(false);
    setImageEditorIndex(null);
    setImageEditorSrc(null);
  };

  const handleImageEditorCancel = () => {
    setImageEditorOpen(false);
    setImageEditorIndex(null);
    setImageEditorSrc(null);
  };

  // Dropzone for image upload
  const onDrop = acceptedFiles => {
    if (acceptedFiles.length === 0) return;

    console.log('🚨 ADMIN PANEL PHOTO UPLOAD DEBUG START!');
    console.log('AdminPanel onDrop: Accepted files:', acceptedFiles.length);
    console.log('AdminPanel onDrop: Current editImages:', editImages.length);
    console.log('AdminPanel onDrop: Current editImageFiles:', editImageFiles.length);

    // Show immediate feedback
    setIsUploading(true);
    setUploadProgress(0);

    // Simulate processing time for immediate feedback
    setTimeout(() => {
      // Add new files to editImageFiles at the end
      setEditImageFiles(prev => {
        const currentFiles = Array.isArray(prev) ? prev : [];
        const newFiles = [...currentFiles, ...acceptedFiles];
        console.log('AdminPanel onDrop: New editImageFiles count:', newFiles.length);
        return newFiles;
      });

      // Add object URLs for preview to editImages at the end
      const newImageUrls = acceptedFiles.map(file => {
        const blobUrl = URL.createObjectURL(file);
        blobUrlsRef.current.add(blobUrl); // Track for cleanup
        return blobUrl;
      });

      setEditImages(prev => {
        const currentImages = Array.isArray(prev) ? prev : [];
        const newImages = [...currentImages, ...newImageUrls];
        console.log('AdminPanel onDrop: New editImages count:', newImages.length);
        console.log(
          'AdminPanel onDrop: Final editImages structure:',
          newImages.map((img, idx) => ({
            index: idx,
            value: img,
            isBlob: img && img.startsWith('blob:'),
            isData: img && img.startsWith('data:'),
            isExisting: img && !img.startsWith('blob:') && !img.startsWith('data:'),
          })),
        );
        console.log('🚨 ADMIN PANEL PHOTO UPLOAD DEBUG END!');
        return newImages;
      });

      // Reset upload state after processing
      setIsUploading(false);
      setUploadProgress(0);
    }, 500); // Small delay to show processing feedback
  };
  const {
    getRootProps,
    getInputProps,
    isDragActive: _isDragActive,
  } = useDropzone({
    onDrop,
    accept: { 'image/*': [] },
    multiple: true,
  });

  const openMapPicker = () => setShowMapPicker(true);
  const closeMapPicker = () => setShowMapPicker(false);
  const handleMapPickerConfirm = picked => {
    console.log('🔍 AdminPanel: MapPicker confirmed with:', picked);
    setEditForm(prev => {
      const updated = { ...prev, location: picked.address, coordinates: picked.coords };
      console.log('🔍 AdminPanel: Updated editForm:', updated);
      return updated;
    });
    setShowMapPicker(false);
  };

  // Add state for pending reports pagination
  const [pendingCurrentPage, setPendingCurrentPage] = useState(1);
  const [pendingPageSize, setPendingPageSize] = useState(8);
  const [pendingSearchTerm, setPendingSearchTerm] = useState('');

  // Filter pending reports based on search term
  const filteredPendingReports = (pendingReports || []).filter(report => {
    if (!pendingSearchTerm.trim()) return true;
    const searchLower = pendingSearchTerm.toLowerCase();
    const referenceNumber = `swarm${report.id}`;
    return (
      report.reporterName?.toLowerCase().includes(searchLower) ||
      report.location?.toLowerCase().includes(searchLower) ||
      report.description?.toLowerCase().includes(searchLower) ||
      report.incidentType?.toLowerCase().includes(searchLower) ||
      report.estimatedIncidentTime?.toLowerCase().includes(searchLower) ||
      report.id?.toString().includes(searchLower) ||
      referenceNumber.includes(searchLower)
    );
  });

  const pendingTotalPages = Math.ceil((filteredPendingReports.length || 0) / pendingPageSize);
  const paginatedPendingReports =
    filteredPendingReports.slice(
      (pendingCurrentPage - 1) * pendingPageSize,
      pendingCurrentPage * pendingPageSize,
    ) || [];

  // Add state for user management pagination and search
  const [userCurrentPage, setUserCurrentPage] = useState(1);
  const [userPageSize, setUserPageSize] = useState(8);
  const [userSearchTerm, setUserSearchTerm] = useState('');

  // Filter users based on search term
  const filteredUserList = (Array.isArray(userList) ? userList : []).filter(user => {
    if (!userSearchTerm.trim()) return true;
    const searchLower = userSearchTerm.toLowerCase();
    return (
      user.name?.toLowerCase().includes(searchLower) ||
      user.email?.toLowerCase().includes(searchLower) ||
      user.number?.toLowerCase().includes(searchLower)
    );
  });

  const userTotalPages = Math.ceil((filteredUserList.length || 0) / userPageSize);
  const paginatedUserList = filteredUserList.slice(
    (userCurrentPage - 1) * userPageSize,
    userCurrentPage * userPageSize,
  );

  // Handle user search
  const handleUserSearch = searchTerm => {
    setUserSearchTerm(searchTerm);
    setUserCurrentPage(1); // Reset to first page when searching
  };

  // Handle report search
  const handleReportSearch = searchTerm => {
    setReportSearchTerm(searchTerm);
    setAdminCurrentPage(1); // Reset to first page when searching
  };

  // Handle pending report search
  const handlePendingSearch = searchTerm => {
    setPendingSearchTerm(searchTerm);
    setPendingCurrentPage(1); // Reset to first page when searching
  };

  return (
    <AdminLayout
      user={user}
      pendingReportsCount={pendingReports ? pendingReports.length : 0}
      onBackToMain={() => setCurrentPage('main')}
      activeTab={activeTab}
      onTabChange={handleTabChange}
    >
      {/* Reports Tab */}
      {activeTab === 'reports' && (
        <section className="bg-white rounded-2xl shadow-soft p-8">
          {/* Action Buttons */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <button
              className="btn btn-primary btn-small"
              onClick={() => {
                setIsAdminMode(true);
                setShowReportForm(true);
                setCurrentPage('main');
              }}
            >
              Create Report
            </button>
            <button
              className="btn btn-success btn-small"
              onClick={() => setShowExportOptions(true)}
            >
              Export Reports
            </button>
            <button
              className="btn btn-secondary btn-small"
              onClick={() => setShowImportModal(true)}
            >
              Import Reports
            </button>
          </div>

          {/* Reports Map */}
          <div className="mb-8">
            <AdminReportsMap
              reports={reports}
              onReportSelect={handleReportSelectFromMap}
              selectedReportId={selectedReportFromMap?.id}
              setSelectedReport={setSelectedReport}
              openEditModal={openEditModal}
            />
          </div>

          {/* Reports Table */}
          {reports.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600 text-lg">No reports submitted yet.</p>
            </div>
          ) : (
            <div className="overflow-x-auto" key={`reports-table-${tableKey}`}>
              {/* Search and Pagination Controls */}
              <div className="flex items-center justify-between mb-4">
                {/* Search Box */}
                <div className="flex items-center gap-2">
                  <label htmlFor="report-search" className="text-sm text-gray-600 font-medium">
                    Search Reports:
                  </label>
                  <input
                    type="text"
                    id="report-search"
                    placeholder="Search by reference number (SWARM), ID, reporter, location, description, type, or date..."
                    value={reportSearchTerm}
                    onChange={e => handleReportSearch(e.target.value)}
                    className="form-input w-80 text-sm"
                  />
                  {reportSearchTerm && (
                    <button
                      onClick={() => handleReportSearch('')}
                      className="text-gray-400 hover:text-gray-600 text-sm"
                      title="Clear search"
                    >
                      ×
                    </button>
                  )}
                </div>
                {/* Pagination Info */}
                <div className="text-sm text-gray-600">
                  Page {adminCurrentPage} of {totalPages} ({filteredReports.length} reports)
                </div>
              </div>

              {/* Search Results Info */}
              {reportSearchTerm && (
                <div className="mb-3 text-sm text-gray-600">
                  Showing {filteredReports.length} of {(reports || []).length} reports matching "
                  {reportSearchTerm}"
                </div>
              )}

              <table className="w-full border-collapse bg-white rounded-xl overflow-hidden shadow-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="border border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">
                      Reference
                    </th>
                    <th className="border border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">
                      Reporter
                    </th>
                    <th className="border border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">
                      Location
                    </th>
                    <th className="border border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">
                      Description
                    </th>
                    <th className="border border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">
                      Status
                    </th>
                    <th className="border border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">
                      Estimated Time
                    </th>
                    <th className="border border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedReports.map(report => {
                    const isRejected = !report.isApproved && report.approvedBy;
                    return (
                      <tr
                        key={report.id}
                        id={`report-row-${report.id}`}
                        className={`border-b border-gray-100 hover:bg-gray-50 transition-colors duration-200 ${
                          selectedReportFromMap?.id === report.id
                            ? 'bg-blue-50 border-blue-200'
                            : ''
                        } ${isRejected ? 'opacity-60 bg-gray-100' : ''}`}
                      >
                        <td className="border border-gray-200 px-4 py-3 font-mono font-medium text-blue-600">
                          SWARM{report.id}
                        </td>
                        <td className="border border-gray-200 px-4 py-3">{report.reporterName}</td>
                        <td className="border border-gray-200 px-4 py-3">{report.location}</td>
                        <td className="border border-gray-200 px-4 py-3">
                          {report.description.length > 50
                            ? `${report.description.substring(0, 50)}...`
                            : report.description}
                        </td>
                        <td className="border border-gray-200 px-4 py-3">
                          {report.isApproved ? (
                            <span className="inline-block bg-success-100 text-success-800 px-2 py-1 rounded-full text-xs font-medium">
                              Approved
                            </span>
                          ) : isRejected ? (
                            <span className="inline-block bg-gray-200 text-gray-600 px-2 py-1 rounded-full text-xs font-medium line-through">
                              Rejected
                            </span>
                          ) : (
                            <span className="inline-block bg-warning-100 text-warning-800 px-2 py-1 rounded-full text-xs font-medium">
                              Pending
                            </span>
                          )}
                        </td>
                        <td className="border border-gray-200 px-4 py-3">
                          {formatTimeForDisplay(
                            report.estimatedIncidentTime,
                            report.timeUnknown,
                            report.isDateRange,
                            report.incidentDateRangeStart,
                            report.incidentDateRangeEnd,
                          )}
                        </td>
                        <td className="border border-gray-200 px-4 py-3">
                          <div className="flex flex-col gap-1">
                            {!report.isApproved && !isRejected && (
                              <div className="flex gap-1">
                                <button
                                  onClick={() =>
                                    showConfirmationModal(
                                      'Approve Report',
                                      'Are you sure you want to approve this report? This will make it publicly visible.',
                                      () => handleApproveReport(report.id),
                                    )
                                  }
                                  className="btn btn-success btn-small flex-1"
                                >
                                  Approve
                                </button>
                                <button
                                  onClick={() =>
                                    showConfirmationModal(
                                      'Reject Report',
                                      'Are you sure you want to reject this report? This action cannot be undone.',
                                      () => handleRejectReport(report.id),
                                    )
                                  }
                                  className="btn btn-error btn-small flex-1"
                                >
                                  Reject
                                </button>
                              </div>
                            )}
                            {isRejected && (
                              <div className="flex gap-1">
                                <button
                                  onClick={() =>
                                    showConfirmationModal(
                                      'Restore Report',
                                      'Are you sure you want to restore this rejected report? It will return to pending status.',
                                      () => handleRestoreReport(report.id),
                                    )
                                  }
                                  className="btn btn-secondary btn-small flex-1"
                                >
                                  Restore
                                </button>
                              </div>
                            )}
                            {report.isApproved && (
                              <div className="flex gap-1">
                                <button
                                  onClick={() =>
                                    showConfirmationModal(
                                      'Revoke Approval',
                                      'Are you sure you want to revoke approval for this report? This will make it pending again.',
                                      () => handleRevokeApproval(report.id),
                                    )
                                  }
                                  className="btn btn-error btn-small border-2 border-red-500 text-red-800 bg-red-100 font-bold shadow-sm hover:bg-red-200 focus:ring-2 focus:ring-red-400 transition-colors flex-1"
                                  aria-label="Revoke approval for this report"
                                >
                                  Revoke
                                </button>
                              </div>
                            )}
                            <div className="flex gap-1">
                              <button
                                className="btn btn-primary btn-small text-xs px-1 py-0.5 flex-1"
                                onClick={() => openEditModal(report)}
                                aria-label="View or Edit report"
                              >
                                View/Edit
                              </button>
                              <button
                                onClick={() =>
                                  showConfirmationModal(
                                    'Delete Report',
                                    'Are you sure you want to delete this report? This action cannot be undone.',
                                    () => handleDeleteReport(report.id),
                                  )
                                }
                                className="btn btn-error btn-small text-xs px-1 py-0.5 flex-1"
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {/* Pagination Controls */}
              <nav className="flex justify-center items-center gap-2 mt-4" aria-label="Pagination">
                <button
                  className="btn btn-secondary px-4 py-2 rounded-lg transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
                  onClick={() => setAdminCurrentPage(p => Math.max(1, p - 1))}
                  disabled={adminCurrentPage === 1}
                  aria-label="Previous page"
                >
                  &larr; Previous
                </button>
                {Array.from({ length: totalPages }, (_, i) => (
                  <button
                    key={i + 1}
                    className={`btn btn-inline px-3 py-2 rounded-lg transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 ${adminCurrentPage === i + 1 ? 'bg-primary-600 text-white font-bold shadow' : 'bg-gray-100 text-gray-700 hover:bg-primary-100'} ${adminCurrentPage === i + 1 ? 'aria-current="page"' : ''}`}
                    onClick={() => setAdminCurrentPage(i + 1)}
                    aria-current={adminCurrentPage === i + 1 ? 'page' : undefined}
                    aria-label={`Page ${i + 1}`}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  className="btn btn-secondary px-4 py-2 rounded-lg transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
                  onClick={() => setAdminCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={adminCurrentPage === totalPages}
                  aria-label="Next page"
                >
                  Next &rarr;
                </button>
              </nav>
            </div>
          )}

          {/* Comprehensive Report Details Modal for Regular Reports */}
          {selectedReport && reports.find(r => r.id === selectedReport) && (
            <div
              ref={viewPanelRef}
              className="mt-6 bg-gray-50 rounded-xl border border-gray-200 overflow-hidden"
            >
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-xl font-semibold text-primary-800">Report Details</h3>
                  <button
                    onClick={() => setSelectedReport(null)}
                    className="btn btn-secondary btn-small"
                    aria-label="Close detailed view"
                  >
                    ✕ Close
                  </button>
                </div>

                {(() => {
                  const report = reports.find(r => r.id === selectedReport);
                  if (!report) return null;
                  const photoCaptions = parseJsonArray(report.photoCaptions);

                  return (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                      {/* Left Column - Basic Information */}
                      <div className="space-y-6">
                        <div>
                          <h4 className="text-lg font-semibold text-primary-800 mb-3">
                            Basic Information
                          </h4>
                          <div className="space-y-3">
                            <div className="flex justify-between">
                              <span className="font-medium text-gray-700">Reporter:</span>
                              <span className="text-gray-800">{report.reporterName}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="font-medium text-gray-700">Email:</span>
                              <span className="text-gray-800">{report.reporterEmail}</span>
                            </div>
                            {report.reporterNumber && (
                              <div className="flex justify-between">
                                <span className="font-medium text-gray-700">Phone:</span>
                                <span className="text-gray-800">{report.reporterNumber}</span>
                              </div>
                            )}
                            <SubmitterIpDetail
                              report={report}
                              otherIds={getSameSubmitterIpIds(report, reports, pendingReports)}
                              labelClass="font-medium text-gray-700"
                              valueClass="text-gray-800"
                            />
                            <div className="flex justify-between">
                              <span className="font-medium text-gray-700">Reported:</span>
                              <span className="text-gray-800">
                                {formatTimeForDisplay(
                                  report.estimatedIncidentTime,
                                  report.timeUnknown,
                                  report.isDateRange,
                                  report.incidentDateRangeStart,
                                  report.incidentDateRangeEnd,
                                )}
                              </span>
                            </div>
                            {report.policeReportTime && (
                              <div className="flex justify-between">
                                <span className="font-medium text-gray-700">
                                  Police Report Time:
                                </span>
                                <span className="text-gray-800">
                                  {formatTimeForDisplay(report.policeReportTime)}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>

                        <div>
                          <h4 className="text-lg font-semibold text-primary-800 mb-3">
                            Incident Details
                          </h4>
                          <div className="space-y-3">
                            {report.incidentType && (
                              <div className="flex justify-between">
                                <span className="font-medium text-gray-700">Type:</span>
                                <span className="text-gray-800">
                                  {formatIncidentType(report.incidentType)}
                                </span>
                              </div>
                            )}
                            {report.witnessedIncident && (
                              <div className="flex justify-between">
                                <span className="font-medium text-gray-700">Witnessed:</span>
                                <span className="text-gray-800">
                                  {report.witnessedIncident === 'yes'
                                    ? 'Yes'
                                    : report.witnessedIncident === 'no'
                                      ? 'No'
                                      : 'Unsure'}
                                </span>
                              </div>
                            )}
                            {report.reportedToPolice && (
                              <div className="flex justify-between">
                                <span className="font-medium text-gray-700">Police Reported:</span>
                                <span className="text-gray-800">
                                  {report.reportedToPolice === 'yes'
                                    ? 'Yes'
                                    : report.reportedToPolice === 'no'
                                      ? 'No'
                                      : 'Unsure'}
                                </span>
                              </div>
                            )}
                            {report.injuries && (
                              <div className="flex justify-between">
                                <span className="font-medium text-gray-700">Injuries:</span>
                                <span className="text-gray-800">
                                  {report.injuries === 'yes'
                                    ? 'Yes'
                                    : report.injuries === 'no'
                                      ? 'No'
                                      : 'Unsure'}
                                </span>
                              </div>
                            )}
                            {report.injuries === 'yes' && report.injuryCount && (
                              <div className="flex justify-between">
                                <span className="font-medium text-gray-700">
                                  Number of Injuries:
                                </span>
                                <span className="text-gray-800">{report.injuryCount}</span>
                              </div>
                            )}
                            {report.injuries === 'yes' && report.injuryNotes && (
                              <div className="flex justify-between">
                                <span className="font-medium text-gray-700">Injury Details:</span>
                                <span className="text-gray-800">{report.injuryNotes}</span>
                              </div>
                            )}
                            <div className="flex justify-between">
                              <span className="font-medium text-gray-700">Location:</span>
                              <span className="text-gray-800">{report.location}</span>
                            </div>
                            {report.coordinates && (
                              <div className="flex justify-between">
                                <span className="font-medium text-gray-700">Coordinates:</span>
                                <span className="text-gray-800 font-mono text-sm">
                                  {typeof report.coordinates === 'string'
                                    ? report.coordinates
                                    : `${report.coordinates.lat}, ${report.coordinates.lng}`}
                                </span>
                              </div>
                            )}
                            {report.policeOfficerName && (
                              <div className="flex justify-between">
                                <span className="font-medium text-gray-700">Police Officer:</span>
                                <span className="text-gray-800">{report.policeOfficerName}</span>
                              </div>
                            )}
                            {report.policeReportNumber && (
                              <div className="flex justify-between">
                                <span className="font-medium text-gray-700">Police Report #:</span>
                                <span className="text-gray-800">{report.policeReportNumber}</span>
                              </div>
                            )}
                          </div>
                        </div>

                        <div>
                          <h4 className="text-lg font-semibold text-primary-800 mb-3">
                            Description
                          </h4>
                          <div className="bg-gray-100 p-4 rounded-lg">
                            <p className="text-gray-900 leading-relaxed">
                              {report.description || 'No description provided.'}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Right Column - Photos and System Info */}
                      <div className="space-y-6">
                        {/* Photos Section */}
                        <div>
                          <h4 className="text-lg font-semibold text-primary-800 mb-3">
                            Photos (
                            {report.photoUrls && Array.isArray(report.photoUrls)
                              ? report.photoUrls.length
                              : 0}
                            )
                          </h4>
                          {report.photoUrls &&
                          Array.isArray(report.photoUrls) &&
                          report.photoUrls.length > 0 ? (
                            <div className="grid grid-cols-2 gap-3">
                              {Array.isArray(report.photoUrls) &&
                                report.photoUrls.map((photoUrl, index) => {
                                  const photoStatus =
                                    report.photoStatuses && report.photoStatuses[index];
                                  const openPhotoPreview = () => {
                                    const photos = Array.isArray(report.photoUrls)
                                      ? report.photoUrls.map((url, idx) => ({
                                          url: getSafeFileUrl(url),
                                          caption: photoCaptions[idx] || '',
                                        }))
                                      : [];
                                    openPreviewModal(
                                      photos[index].url,
                                      photos[index].caption,
                                      photos,
                                      index,
                                    );
                                  };
                                  return (
                                    <div key={index} className="relative">
                                      <button
                                        type="button"
                                        className="block w-full p-0 border-0 bg-transparent cursor-pointer"
                                        onClick={openPhotoPreview}
                                        title="Click to view full size"
                                      >
                                        <img
                                          src={getSafeFileUrl(photoUrl)}
                                          alt={`${index + 1}${photoCaptions[index] ? `: ${photoCaptions[index]}` : ''}`}
                                          className={`w-full h-32 object-cover rounded-lg border hover:opacity-90 transition-opacity ${
                                            photoStatus === 'approved'
                                              ? 'border-success-300'
                                              : photoStatus === 'rejected'
                                                ? 'border-error-300'
                                                : 'border-gray-200'
                                          }`}
                                        />
                                      </button>

                                      {/* Photo Status Badge */}
                                      {photoStatus && (
                                        <div
                                          className={`absolute top-1 right-1 px-2 py-1 rounded-full text-xs font-medium ${
                                            photoStatus === 'approved'
                                              ? 'bg-success-100 text-success-800'
                                              : photoStatus === 'rejected'
                                                ? 'bg-error-100 text-error-800'
                                                : 'bg-warning-100 text-warning-800'
                                          }`}
                                        >
                                          {photoStatus === 'approved'
                                            ? '✓ Approved'
                                            : photoStatus === 'rejected'
                                              ? '✗ Rejected'
                                              : 'Pending'}
                                        </div>
                                      )}

                                      {/* Photo Caption */}
                                      {photoCaptions[index] && (
                                        <div className="mt-1 text-xs text-gray-600 truncate">
                                          {photoCaptions[index]}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                            </div>
                          ) : (
                            <div className="flex items-center justify-center h-32 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                              <img
                                src={getIncidentTypeIconUrl(report.incidentType, 64)}
                                alt={`${formatIncidentType(report.incidentType)} incident`}
                                className="w-16 h-16 opacity-60"
                              />
                              <span className="ml-3 text-sm text-gray-500">
                                No photos available
                              </span>
                            </div>
                          )}
                        </div>

                        <div>
                          <h4 className="text-lg font-semibold text-primary-800 mb-3">
                            System Information
                          </h4>
                          <div className="space-y-3 text-sm">
                            <div className="flex justify-between">
                              <span className="font-medium text-gray-700">Report ID:</span>
                              <span className="text-gray-800 font-mono">{report.id}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="font-medium text-gray-700">Created:</span>
                              <span className="text-gray-800">
                                {formatTimeForDisplay(report.createdAt)}
                              </span>
                            </div>
                            {report.updatedAt && (
                              <div className="flex justify-between">
                                <span className="font-medium text-gray-700">Last Updated:</span>
                                <span className="text-gray-800">
                                  {formatTimeForDisplay(report.updatedAt)}
                                </span>
                              </div>
                            )}
                            <div className="flex justify-between">
                              <span className="font-medium text-gray-700">Submitted by:</span>
                              <span className="text-gray-800">{report.userEmail}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="font-medium text-gray-700">Status:</span>
                              <span
                                className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  report.isApproved
                                    ? 'bg-success-100 text-success-800'
                                    : !report.isApproved && report.approvedBy
                                      ? 'bg-gray-200 text-gray-600 line-through'
                                      : 'bg-warning-100 text-warning-800'
                                }`}
                              >
                                {report.isApproved
                                  ? 'Approved'
                                  : !report.isApproved && report.approvedBy
                                    ? 'Rejected'
                                    : 'Pending'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          )}
        </section>
      )}

      {/* Archived Reports Tab */}
      {activeTab === 'archived' && (
        <section className="bg-white rounded-2xl shadow-soft p-8">
          <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-700 mb-2">
              Archived Reports ({archivedReports.length})
            </h3>
            <p className="text-gray-600">
              These reports have been rejected and are archived. They are not visible on the public
              map.
            </p>
          </div>

          {archivedReports.length > 0 ? (
            <div>
              {/* Search */}
              <div className="mb-4">
                <input
                  type="text"
                  placeholder="Search archived reports by name, location, description, or reference..."
                  value={archivedSearchTerm}
                  onChange={e => {
                    setArchivedSearchTerm(e.target.value);
                    setArchivedCurrentPage(1);
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              {/* Pagination info */}
              <div className="mb-4 text-sm text-gray-600">
                Page {archivedCurrentPage} of {archivedTotalPages} ({archivedReports.length}{' '}
                archived reports)
              </div>

              {/* Archived Reports Table */}
              <table className="w-full border-collapse bg-white rounded-xl overflow-hidden shadow-sm">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="border border-gray-200 px-4 py-3 text-left font-semibold text-gray-700">
                      Reference
                    </th>
                    <th className="border border-gray-200 px-4 py-3 text-left font-semibold text-gray-700">
                      Reporter
                    </th>
                    <th className="border border-gray-200 px-4 py-3 text-left font-semibold text-gray-700">
                      Location
                    </th>
                    <th className="border border-gray-200 px-4 py-3 text-left font-semibold text-gray-700">
                      Description
                    </th>
                    <th className="border border-gray-200 px-4 py-3 text-left font-semibold text-gray-700">
                      Rejected By
                    </th>
                    <th className="border border-gray-200 px-4 py-3 text-left font-semibold text-gray-700">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedArchivedReports.map(report => (
                    <tr
                      key={report.id}
                      className="border-b border-gray-100 bg-gray-50 opacity-70 hover:opacity-100 transition-opacity"
                    >
                      <td className="border border-gray-200 px-4 py-3 font-mono text-gray-500">
                        SWARM{report.id}
                      </td>
                      <td className="border border-gray-200 px-4 py-3 text-gray-600">
                        {report.reporterName}
                      </td>
                      <td className="border border-gray-200 px-4 py-3 text-gray-600">
                        {report.location}
                      </td>
                      <td className="border border-gray-200 px-4 py-3 text-gray-600">
                        {report.description?.length > 50
                          ? `${report.description.substring(0, 50)}...`
                          : report.description}
                      </td>
                      <td className="border border-gray-200 px-4 py-3 text-gray-600">
                        {report.approvedBy}
                      </td>
                      <td className="border border-gray-200 px-4 py-3">
                        <div className="flex flex-col gap-1">
                          <div className="flex gap-1">
                            <button
                              onClick={() =>
                                showConfirmationModal(
                                  'Restore Report',
                                  'Are you sure you want to restore this report? It will return to pending status for review.',
                                  () => handleRestoreReport(report.id),
                                )
                              }
                              className="btn btn-success btn-small flex-1"
                            >
                              Restore
                            </button>
                            <button
                              onClick={() => openEditModal(report)}
                              className="btn btn-secondary btn-small flex-1"
                            >
                              View
                            </button>
                          </div>
                          <button
                            onClick={() =>
                              showConfirmationModal(
                                'Permanently Delete',
                                'Are you sure you want to permanently delete this report? This cannot be undone.',
                                () => handleDeleteReport(report.id),
                              )
                            }
                            className="btn btn-error btn-small text-xs"
                          >
                            Delete Permanently
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Pagination Controls */}
              {archivedTotalPages > 1 && (
                <nav
                  className="flex justify-center items-center gap-2 mt-4"
                  aria-label="Archived pagination"
                >
                  <button
                    className="btn btn-secondary px-4 py-2 rounded-lg disabled:opacity-50"
                    onClick={() => setArchivedCurrentPage(p => Math.max(1, p - 1))}
                    disabled={archivedCurrentPage === 1}
                  >
                    &larr; Previous
                  </button>
                  {Array.from({ length: archivedTotalPages }, (_, i) => (
                    <button
                      key={i + 1}
                      className={`btn btn-inline px-3 py-2 rounded-lg ${
                        archivedCurrentPage === i + 1
                          ? 'bg-gray-600 text-white font-bold'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                      onClick={() => setArchivedCurrentPage(i + 1)}
                    >
                      {i + 1}
                    </button>
                  ))}
                  <button
                    className="btn btn-secondary px-4 py-2 rounded-lg disabled:opacity-50"
                    onClick={() => setArchivedCurrentPage(p => Math.min(archivedTotalPages, p + 1))}
                    disabled={archivedCurrentPage === archivedTotalPages}
                  >
                    Next &rarr;
                  </button>
                </nav>
              )}
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <svg
                className="mx-auto h-12 w-12 text-gray-400 mb-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"
                />
              </svg>
              <p className="text-lg font-medium">No archived reports</p>
              <p className="text-sm">Rejected reports will appear here</p>
            </div>
          )}
        </section>
      )}

      {/* Moderation Tab */}
      {activeTab === 'moderation' && (
        <section className="bg-white rounded-2xl shadow-soft p-8">
          {pendingReports && pendingReports.length > 0 ? (
            <div>
              <div className="mb-6 p-4 bg-warning-50 border border-warning-200 rounded-lg">
                <h3 className="text-lg font-semibold text-warning-800 mb-2">
                  Pending Reports ({pendingReports.length})
                </h3>
                <p className="text-warning-700">
                  These reports are awaiting admin approval before becoming publicly visible.
                </p>
              </div>

              <div className="overflow-x-auto">
                {/* Search and Pagination Controls */}
                <div className="flex items-center justify-between mb-4">
                  {/* Search Box */}
                  <div className="flex items-center gap-2">
                    <label htmlFor="pending-search" className="text-sm text-gray-600 font-medium">
                      Search Pending Reports:
                    </label>
                    <input
                      type="text"
                      id="pending-search"
                      placeholder="Search by reference number (SWARM), ID, reporter, location, description, type, or date..."
                      value={pendingSearchTerm}
                      onChange={e => handlePendingSearch(e.target.value)}
                      className="form-input w-80 text-sm"
                    />
                    {pendingSearchTerm && (
                      <button
                        onClick={() => handlePendingSearch('')}
                        className="text-gray-400 hover:text-gray-600 text-sm"
                        title="Clear search"
                      >
                        ×
                      </button>
                    )}
                  </div>
                  {/* Pagination Controls */}
                  <div className="flex items-center gap-2">
                    <label htmlFor="pending-page-size" className="text-xs text-gray-600">
                      Rows per page:
                    </label>
                    <select
                      id="pending-page-size"
                      value={pendingPageSize}
                      onChange={e => {
                        setPendingPageSize(Number(e.target.value));
                        setPendingCurrentPage(1);
                      }}
                      className="form-input text-xs w-16"
                    >
                      {[8, 16, 32, 64].map(size => (
                        <option key={size} value={size}>
                          {size}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Search Results Info */}
                {pendingSearchTerm && (
                  <div className="mb-3 text-sm text-gray-600">
                    Showing {filteredPendingReports.length} of {(pendingReports || []).length}{' '}
                    pending reports matching "{pendingSearchTerm}"
                  </div>
                )}
                <table className="w-full border-collapse bg-white rounded-xl overflow-hidden shadow-sm">
                  <thead className="bg-warning-50">
                    <tr>
                      <th className="border border-warning-200 px-4 py-3 text-left font-semibold text-warning-800">
                        Reference
                      </th>
                      <th className="border border-warning-200 px-4 py-3 text-left font-semibold text-warning-800">
                        Reporter
                      </th>
                      <th className="border border-warning-200 px-4 py-3 text-left font-semibold text-warning-800">
                        Location
                      </th>
                      <th className="border border-warning-200 px-4 py-3 text-left font-semibold text-warning-800">
                        Description
                      </th>
                      <th className="border border-warning-200 px-4 py-3 text-left font-semibold text-warning-800">
                        Date
                      </th>
                      <th className="border border-warning-200 px-4 py-3 text-left font-semibold text-warning-800">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedPendingReports.map(report => (
                      <tr
                        key={report.id}
                        className="border-b border-warning-100 hover:bg-warning-50"
                      >
                        <td className="border border-warning-200 px-4 py-3 font-mono font-medium text-blue-600">
                          SWARM{report.id}
                        </td>
                        <td className="border border-warning-200 px-4 py-3">
                          {report.reporterName}
                        </td>
                        <td className="border border-warning-200 px-4 py-3">{report.location}</td>
                        <td className="border border-warning-200 px-4 py-3">
                          {report.description.length > 50
                            ? `${report.description.substring(0, 50)}...`
                            : report.description}
                        </td>
                        <td className="border border-warning-200 px-4 py-3">
                          {report.estimatedIncidentTime}
                        </td>
                        <td className="border border-warning-200 px-4 py-3">
                          <div className="flex flex-col gap-1">
                            <div className="flex gap-1">
                              <button
                                onClick={() =>
                                  showConfirmationModal(
                                    'Approve Report',
                                    'Are you sure you want to approve this report? This will make it publicly visible.',
                                    () => handleApproveReport(report.id),
                                  )
                                }
                                className="btn btn-success btn-small flex-1"
                              >
                                Approve
                              </button>
                              <button
                                onClick={() =>
                                  showConfirmationModal(
                                    'Reject Report',
                                    'Are you sure you want to reject this report? This action cannot be undone.',
                                    () => handleRejectReport(report.id),
                                  )
                                }
                                className="btn btn-error btn-small flex-1"
                              >
                                Reject
                              </button>
                            </div>
                            <button
                              onClick={() => openEditModal(report)}
                              className="btn btn-inline btn-small text-xs px-1 py-0.5 flex-1"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() =>
                                showConfirmationModal(
                                  'Delete Report',
                                  'Are you sure you want to delete this report? This action cannot be undone.',
                                  () => handleDeleteReport(report.id),
                                )
                              }
                              className="btn btn-error btn-small text-xs px-1 py-0.5 flex-1"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <nav
                  className="flex justify-center items-center gap-2 mt-2"
                  aria-label="Pagination"
                >
                  <button
                    className="btn btn-secondary btn-xs"
                    onClick={() => setPendingCurrentPage(p => Math.max(1, p - 1))}
                    disabled={pendingCurrentPage === 1}
                  >
                    &larr;
                  </button>
                  {Array.from({ length: pendingTotalPages }, (_, i) => (
                    <button
                      key={i + 1}
                      className={`btn btn-inline btn-xs ${pendingCurrentPage === i + 1 ? 'bg-primary-600 text-white font-bold shadow' : 'bg-gray-100 text-gray-700 hover:bg-primary-100'}`}
                      onClick={() => setPendingCurrentPage(i + 1)}
                    >
                      {i + 1}
                    </button>
                  ))}
                  <button
                    className="btn btn-secondary btn-xs"
                    onClick={() => setPendingCurrentPage(p => Math.min(pendingTotalPages, p + 1))}
                    disabled={pendingCurrentPage === pendingTotalPages}
                  >
                    &rarr;
                  </button>
                </nav>
              </div>

              {/* Comprehensive Report Details Modal for Pending Reports */}
              {selectedReport && pendingReports.find(r => r.id === selectedReport) && (
                <div
                  ref={viewPanelRef}
                  className="mt-6 bg-warning-50 rounded-xl border border-warning-200 overflow-hidden"
                >
                  <div className="p-6">
                    <div className="flex justify-between items-center mb-6">
                      <h3 className="text-xl font-semibold text-warning-800">
                        Pending Report Details
                      </h3>
                      <button
                        onClick={() => setSelectedReport(null)}
                        className="btn btn-secondary btn-small"
                        aria-label="Close detailed view"
                      >
                        ✕ Close
                      </button>
                    </div>

                    {(() => {
                      const report = pendingReports.find(r => r.id === selectedReport);
                      if (!report) return null;
                      const photoCaptions = parseJsonArray(report.photoCaptions);

                      return (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                          {/* Left Column - Basic Information */}
                          <div className="space-y-6">
                            <div>
                              <h4 className="text-lg font-semibold text-warning-800 mb-3">
                                Basic Information
                              </h4>
                              <div className="space-y-3">
                                <div className="flex justify-between">
                                  <span className="font-medium text-warning-700">Reporter:</span>
                                  <span className="text-warning-800">{report.reporterName}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="font-medium text-warning-700">Email:</span>
                                  <span className="text-warning-800">{report.reporterEmail}</span>
                                </div>
                                {report.reporterNumber && (
                                  <div className="flex justify-between">
                                    <span className="font-medium text-warning-700">Phone:</span>
                                    <span className="text-warning-800">
                                      {report.reporterNumber}
                                    </span>
                                  </div>
                                )}
                                <SubmitterIpDetail
                                  report={report}
                                  otherIds={getSameSubmitterIpIds(report, reports, pendingReports)}
                                  labelClass="font-medium text-warning-700"
                                  valueClass="text-warning-800"
                                />
                                <div className="flex justify-between">
                                  <span className="font-medium text-warning-700">Reported:</span>
                                  <span className="text-warning-800">
                                    {formatTimeForDisplay(
                                      report.estimatedIncidentTime,
                                      report.timeUnknown,
                                      report.isDateRange,
                                      report.incidentDateRangeStart,
                                      report.incidentDateRangeEnd,
                                    )}
                                  </span>
                                </div>
                                {report.policeReportTime && (
                                  <div className="flex justify-between">
                                    <span className="font-medium text-warning-700">
                                      Police Report Time:
                                    </span>
                                    <span className="text-warning-800">
                                      {formatTimeForDisplay(report.policeReportTime)}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>

                            <div>
                              <h4 className="text-lg font-semibold text-warning-800 mb-3">
                                Incident Details
                              </h4>
                              <div className="space-y-3">
                                {report.incidentType && (
                                  <div className="flex justify-between">
                                    <span className="font-medium text-warning-700">Type:</span>
                                    <span className="text-warning-800">
                                      {formatIncidentType(report.incidentType)}
                                    </span>
                                  </div>
                                )}
                                {report.witnessedIncident && (
                                  <div className="flex justify-between">
                                    <span className="font-medium text-warning-700">Witnessed:</span>
                                    <span className="text-warning-800">
                                      {report.witnessedIncident === 'yes'
                                        ? 'Yes'
                                        : report.witnessedIncident === 'no'
                                          ? 'No'
                                          : 'Unsure'}
                                    </span>
                                  </div>
                                )}
                                {report.reportedToPolice && (
                                  <div className="flex justify-between">
                                    <span className="font-medium text-warning-700">
                                      Police Reported:
                                    </span>
                                    <span className="text-warning-800">
                                      {report.reportedToPolice === 'yes'
                                        ? 'Yes'
                                        : report.reportedToPolice === 'no'
                                          ? 'No'
                                          : 'Unsure'}
                                    </span>
                                  </div>
                                )}
                                {report.injuries && (
                                  <div className="flex justify-between">
                                    <span className="font-medium text-warning-700">Injuries:</span>
                                    <span className="text-warning-800">
                                      {report.injuries === 'yes'
                                        ? 'Yes'
                                        : report.injuries === 'no'
                                          ? 'No'
                                          : 'Unsure'}
                                    </span>
                                  </div>
                                )}
                                {report.injuries === 'yes' && report.injuryCount && (
                                  <div className="flex justify-between">
                                    <span className="font-medium text-warning-700">
                                      Number of Injuries:
                                    </span>
                                    <span className="text-warning-800">{report.injuryCount}</span>
                                  </div>
                                )}
                                {report.injuries === 'yes' && report.injuryNotes && (
                                  <div className="flex justify-between">
                                    <span className="font-medium text-warning-700">
                                      Injury Details:
                                    </span>
                                    <span className="text-warning-800">{report.injuryNotes}</span>
                                  </div>
                                )}
                                <div className="flex justify-between">
                                  <span className="font-medium text-warning-700">Location:</span>
                                  <span className="text-warning-800">{report.location}</span>
                                </div>
                                {report.coordinates && (
                                  <div className="flex justify-between">
                                    <span className="font-medium text-warning-700">
                                      Coordinates:
                                    </span>
                                    <span className="text-warning-800 font-mono text-sm">
                                      {typeof report.coordinates === 'string'
                                        ? report.coordinates
                                        : `${report.coordinates.lat}, ${report.coordinates.lng}`}
                                    </span>
                                  </div>
                                )}
                                {report.policeOfficerName && (
                                  <div className="flex justify-between">
                                    <span className="font-medium text-warning-700">
                                      Police Officer:
                                    </span>
                                    <span className="text-warning-800">
                                      {report.policeOfficerName}
                                    </span>
                                  </div>
                                )}
                                {report.policeReportNumber && (
                                  <div className="flex justify-between">
                                    <span className="font-medium text-warning-700">
                                      Police Report #:
                                    </span>
                                    <span className="text-warning-800">
                                      {report.policeReportNumber}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>

                            <div>
                              <h4 className="text-lg font-semibold text-warning-800 mb-3">
                                Description
                              </h4>
                              <div className="bg-warning-100 p-4 rounded-lg">
                                <p className="text-warning-900 leading-relaxed">
                                  {report.description || 'No description provided.'}
                                </p>
                              </div>
                            </div>
                          </div>

                          {/* Right Column - Photos and Additional Info */}
                          <div className="space-y-6">
                            <div>
                              <h4 className="text-lg font-semibold text-warning-800 mb-3">
                                Photos (
                                {report.photoUrls && Array.isArray(report.photoUrls)
                                  ? report.photoUrls.length
                                  : 0}
                                )
                              </h4>
                              {report.photoUrls &&
                              Array.isArray(report.photoUrls) &&
                              report.photoUrls.length > 0 ? (
                                <div className="grid grid-cols-2 gap-4">
                                  {Array.isArray(report.photoUrls) &&
                                    report.photoUrls.map((photoUrl, index) => {
                                      const photoStatus =
                                        report.photoApprovalStatus &&
                                        report.photoApprovalStatus[index];
                                      const openPhotoPreview = () => {
                                        const photos = Array.isArray(report.photoUrls)
                                          ? report.photoUrls.map((url, idx) => ({
                                              url: getSafeFileUrl(url),
                                              caption: photoCaptions[idx] || '',
                                            }))
                                          : [];
                                        openPreviewModal(
                                          photos[index].url,
                                          photos[index].caption,
                                          photos,
                                          index,
                                        );
                                      };
                                      return (
                                        <div key={index} className="relative group">
                                          <div className="relative">
                                            <button
                                              type="button"
                                              className="block w-full p-0 border-0 bg-transparent cursor-pointer"
                                              onClick={openPhotoPreview}
                                              title="Click to view full size"
                                            >
                                              <img
                                                src={getSafeFileUrl(photoUrl)}
                                                alt={`${index + 1}${photoCaptions[index] ? `: ${photoCaptions[index]}` : ''}`}
                                                className={`w-full h-32 object-cover rounded-lg border hover:opacity-90 transition-opacity ${
                                                  photoStatus === 'approved'
                                                    ? 'border-success-300'
                                                    : photoStatus === 'rejected'
                                                      ? 'border-error-300'
                                                      : 'border-warning-200'
                                                }`}
                                              />
                                            </button>

                                            {/* Photo Status Badge */}
                                            {photoStatus && (
                                              <div
                                                className={`absolute top-2 right-2 px-2 py-1 rounded-full text-xs font-medium ${
                                                  photoStatus === 'approved'
                                                    ? 'bg-success-100 text-success-800'
                                                    : photoStatus === 'rejected'
                                                      ? 'bg-error-100 text-error-800'
                                                      : 'bg-warning-100 text-warning-800'
                                                }`}
                                              >
                                                {photoStatus === 'approved'
                                                  ? '✓ Approved'
                                                  : photoStatus === 'rejected'
                                                    ? '✗ Rejected'
                                                    : 'Pending'}
                                              </div>
                                            )}

                                            {/* Photo Approval Controls */}
                                            <div className="absolute bottom-2 left-2 right-2 flex gap-1">
                                              <button
                                                onClick={e => {
                                                  e.stopPropagation();
                                                  showConfirmationModal(
                                                    'Approve Photo',
                                                    'Are you sure you want to approve this photo?',
                                                    () => handleApprovePhoto(report.id, index),
                                                  );
                                                }}
                                                className={`flex-1 px-2 py-1 text-xs rounded font-medium transition-colors ${
                                                  photoStatus === 'approved'
                                                    ? 'bg-success-200 text-success-800 cursor-default'
                                                    : 'bg-success-100 text-success-700 hover:bg-success-200'
                                                }`}
                                                disabled={photoStatus === 'approved'}
                                                title="Approve this photo"
                                              >
                                                ✓ Approve
                                              </button>
                                              <button
                                                onClick={e => {
                                                  e.stopPropagation();
                                                  showConfirmationModal(
                                                    'Reject Photo',
                                                    'Are you sure you want to reject this photo?',
                                                    () => handleRejectPhoto(report.id, index),
                                                  );
                                                }}
                                                className={`flex-1 px-2 py-1 text-xs rounded font-medium transition-colors ${
                                                  photoStatus === 'rejected'
                                                    ? 'bg-error-200 text-error-800 cursor-default'
                                                    : 'bg-error-100 text-error-700 hover:bg-error-200'
                                                }`}
                                                disabled={photoStatus === 'rejected'}
                                                title="Reject this photo"
                                              >
                                                ✗ Reject
                                              </button>
                                            </div>
                                          </div>

                                          {photoCaptions[index] && (
                                            <div className="absolute bottom-12 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-2 rounded-b-lg">
                                              {photoCaptions[index]}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                </div>
                              ) : (
                                <div className="flex items-center justify-center h-32 bg-warning-50 rounded-lg border-2 border-dashed border-warning-300">
                                  <img
                                    src={getIncidentTypeIconUrl(report.incidentType, 64)}
                                    alt={`${formatIncidentType(report.incidentType)} incident`}
                                    className="w-16 h-16 opacity-60"
                                  />
                                  <span className="ml-3 text-sm text-warning-500">
                                    No photos available
                                  </span>
                                </div>
                              )}
                            </div>

                            <div>
                              <h4 className="text-lg font-semibold text-warning-800 mb-3">
                                System Information
                              </h4>
                              <div className="space-y-3 text-sm">
                                <div className="flex justify-between">
                                  <span className="font-medium text-warning-700">Report ID:</span>
                                  <span className="text-warning-800 font-mono">{report.id}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="font-medium text-warning-700">Created:</span>
                                  <span className="text-warning-800">
                                    {formatTimeForDisplay(report.createdAt)}
                                  </span>
                                </div>
                                {report.updatedAt && (
                                  <div className="flex justify-between">
                                    <span className="font-medium text-warning-700">
                                      Last Updated:
                                    </span>
                                    <span className="text-warning-800">
                                      {formatTimeForDisplay(report.updatedAt)}
                                    </span>
                                  </div>
                                )}
                                <div className="flex justify-between">
                                  <span className="font-medium text-warning-700">
                                    Submitted by:
                                  </span>
                                  <span className="text-warning-800">{report.userEmail}</span>
                                </div>
                              </div>
                            </div>

                            {/* Moderation Actions */}
                            <div className="pt-4 border-t border-warning-200">
                              <h4 className="text-lg font-semibold text-warning-800 mb-3">
                                Moderation Actions
                              </h4>
                              <div className="flex gap-3 flex-wrap">
                                <button
                                  className="btn btn-success btn-small"
                                  onClick={() =>
                                    showConfirmationModal(
                                      'Approve Report',
                                      'Are you sure you want to approve this report? This will make it publicly visible.',
                                      () => handleApproveReport(report.id),
                                    )
                                  }
                                >
                                  ✓ Approve Report
                                </button>
                                <button
                                  className="btn btn-error btn-small"
                                  onClick={() =>
                                    showConfirmationModal(
                                      'Reject Report',
                                      'Are you sure you want to reject this report? This action cannot be undone.',
                                      () => handleRejectReport(report.id),
                                    )
                                  }
                                >
                                  ✗ Reject Report
                                </button>
                                <button
                                  className="btn btn-error btn-small"
                                  onClick={() =>
                                    showConfirmationModal(
                                      'Delete Report',
                                      'Are you sure you want to delete this report? This action cannot be undone.',
                                      () => handleDeleteReport(report.id),
                                    )
                                  }
                                >
                                  🗑️ Delete Report
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12">
              <h3 className="text-xl font-semibold text-success-800 mb-2">All Caught Up!</h3>
              <p className="text-gray-600">No reports are currently pending approval.</p>
            </div>
          )}
        </section>
      )}

      {/* Extra Notes Tab */}
      {activeTab === 'extra-notes' && (
        <section className="bg-white rounded-2xl shadow-soft p-8">
          <PendingExtraNotesManager
            onNotesUpdate={() => {
              // Refresh the reports list if needed
              if (setReports) {
                // This could trigger a refresh of reports to show updated notes
              }
            }}
          />
        </section>
      )}

      {/* Users Tab */}
      {activeTab === 'users' && (
        <section className="bg-white rounded-2xl shadow-soft p-8">
          {/* Quick Alert Actions */}
          <div className="mb-6 p-4 bg-gray-50 rounded-xl border border-gray-200">
            <h3 className="text-lg font-semibold text-primary-800 mb-3">Quick Alert Actions</h3>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() =>
                  showConfirmationModal(
                    'Enable All Alerts',
                    `Are you sure you want to enable all alert types for all ${(Array.isArray(userList) ? userList : []).length} users?`,
                    () =>
                      (Array.isArray(userList) ? userList : []).forEach(user =>
                        handleAllAlerts(user.email),
                      ),
                  )
                }
                className="btn btn-success btn-small"
              >
                Enable All Alerts for All Users
              </button>
              <button
                onClick={() =>
                  showConfirmationModal(
                    'Disable All Alerts',
                    `Are you sure you want to disable all alert types for all ${(Array.isArray(userList) ? userList : []).length} users?`,
                    () =>
                      (Array.isArray(userList) ? userList : []).forEach(user =>
                        handleNoAlerts(user.email),
                      ),
                  )
                }
                className="btn btn-secondary btn-small"
              >
                Disable All Alerts for All Users
              </button>
              <button
                onClick={() =>
                  showConfirmationModal(
                    'Toggle Report Alerts',
                    `Are you sure you want to toggle report alerts for all ${(Array.isArray(userList) ? userList : []).length} users?`,
                    () =>
                      (Array.isArray(userList) ? userList : []).forEach(user =>
                        toggleUserAlertPreference(user.email, 'reports'),
                      ),
                  )
                }
                className="btn btn-primary btn-small"
              >
                Toggle Report Alerts for All Users
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            {/* Search and Pagination Controls */}
            <div className="flex items-center justify-between mb-4">
              {/* Search Box */}
              <div className="flex items-center gap-2">
                <label htmlFor="user-search" className="text-sm text-gray-600 font-medium">
                  Search Users:
                </label>
                <input
                  type="text"
                  id="user-search"
                  placeholder="Search by name, email, or phone..."
                  value={userSearchTerm}
                  onChange={e => handleUserSearch(e.target.value)}
                  className="form-input w-64 text-sm"
                />
                {userSearchTerm && (
                  <button
                    onClick={() => handleUserSearch('')}
                    className="text-gray-400 hover:text-gray-600 text-sm"
                    title="Clear search"
                  >
                    ×
                  </button>
                )}
              </div>
              {/* Pagination Controls */}
              <div className="flex items-center gap-2">
                <label htmlFor="user-page-size" className="text-xs text-gray-600">
                  Rows per page:
                </label>
                <select
                  id="user-page-size"
                  value={userPageSize}
                  onChange={e => {
                    setUserPageSize(Number(e.target.value));
                    setUserCurrentPage(1);
                  }}
                  className="form-input text-xs w-16"
                >
                  {[8, 16, 32, 64].map(size => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Search Results Info */}
            {userSearchTerm && (
              <div className="mb-3 text-sm text-gray-600">
                Showing {filteredUserList.length} of{' '}
                {(Array.isArray(userList) ? userList : []).length} users matching "{userSearchTerm}"
              </div>
            )}
            <table className="w-full border-collapse bg-white rounded-xl overflow-hidden shadow-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="border border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">
                    Name
                  </th>
                  <th className="border border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">
                    Email
                  </th>
                  <th className="border border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">
                    Phone
                  </th>
                  <th className="border border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">
                    Admin
                  </th>
                  <th className="border border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">
                    2FA
                  </th>
                  <th className="border border-gray-200 px-4 py-3 text-left font-semibold text-gray-900 text-xs">
                    All Alerts
                  </th>
                  <th className="border border-gray-200 px-4 py-3 text-left font-semibold text-gray-900 text-xs">
                    Reports
                  </th>
                  <th className="border border-gray-200 px-4 py-3 text-left font-semibold text-gray-900 text-xs">
                    Users
                  </th>
                  <th className="border border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginatedUserList.map(user => {
                  const prefs = userAlertPreferences[user.email] || {};
                  return (
                    <tr key={user.email} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="border border-gray-200 px-4 py-3">{user.name}</td>
                      <td className="border border-gray-200 px-4 py-3">{user.email}</td>
                      <td className="border border-gray-200 px-4 py-3">{user.number || '-'}</td>
                      <td className="border border-gray-200 px-4 py-3">
                        <input
                          type="checkbox"
                          checked={user.isAdmin}
                          onChange={() =>
                            showConfirmationModal(
                              user.isAdmin ? 'Revoke Admin Access' : 'Grant Admin Access',
                              `Are you sure you want to ${user.isAdmin ? 'revoke' : 'grant'} admin access for user "${user.name}" (${user.email})?`,
                              () => handleToggleAdmin(user.email),
                            )
                          }
                          className="w-4 h-4 text-primary-600 bg-gray-100 border-gray-300 rounded focus:ring-primary-500"
                        />
                      </td>
                      <td className="border border-gray-200 px-4 py-3">
                        <input
                          type="checkbox"
                          checked={user.is2FAEnabled || false}
                          onChange={() =>
                            showConfirmationModal(
                              'Disable 2FA',
                              `Are you sure you want to disable 2FA for user "${user.name}" (${user.email})?`,
                              () => handleToggle2FA(user.email),
                            )
                          }
                          disabled={!user.is2FAEnabled}
                          className="w-4 h-4 text-primary-600 bg-gray-100 border-gray-300 rounded focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                          title={
                            user.is2FAEnabled
                              ? '2FA Enabled - Click to disable'
                              : '2FA Disabled - Users must enable 2FA themselves'
                          }
                        />
                      </td>
                      <td className="border border-gray-200 px-4 py-3">
                        <input
                          type="checkbox"
                          checked={prefs.all || false}
                          onChange={() => handleToggleUserAlertPreference(user.email, 'all')}
                          className="w-4 h-4 text-primary-600 bg-gray-100 border-gray-300 rounded focus:ring-primary-500"
                          title="All Alerts"
                        />
                      </td>
                      <td className="border border-gray-200 px-4 py-3">
                        <input
                          type="checkbox"
                          checked={prefs.reports || false}
                          onChange={() => handleToggleUserAlertPreference(user.email, 'reports')}
                          className="w-4 h-4 text-primary-600 bg-gray-100 border-gray-300 rounded focus:ring-primary-500"
                          title="Incident Reports"
                        />
                      </td>
                      <td className="border border-gray-200 px-4 py-3">
                        <input
                          type="checkbox"
                          checked={prefs.users || false}
                          onChange={() => handleToggleUserAlertPreference(user.email, 'users')}
                          className="w-4 h-4 text-primary-600 bg-gray-100 border-gray-300 rounded focus:ring-primary-500"
                          title="User Management"
                        />
                      </td>
                      <td className="border border-gray-200 px-4 py-3">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEditUser(user)}
                            className="btn btn-inline btn-small"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => openPasswordResetModal(user)}
                            className="btn btn-warning btn-small"
                          >
                            Reset Password
                          </button>
                          <button
                            onClick={() =>
                              showConfirmationModal(
                                'Delete User',
                                `Are you sure you want to delete user "${user.name}" (${user.email})? This action cannot be undone.`,
                                () => handleDeleteUser(user.email),
                              )
                            }
                            className="btn btn-error btn-small"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <nav className="flex justify-center items-center gap-2 mt-2" aria-label="Pagination">
              <button
                className="btn btn-secondary btn-xs"
                onClick={() => setUserCurrentPage(p => Math.max(1, p - 1))}
                disabled={userCurrentPage === 1}
              >
                &larr;
              </button>
              {Array.from({ length: userTotalPages }, (_, i) => (
                <button
                  key={i + 1}
                  className={`btn btn-inline btn-xs ${userCurrentPage === i + 1 ? 'bg-primary-600 text-white font-bold shadow' : 'bg-gray-100 text-gray-700 hover:bg-primary-100'}`}
                  onClick={() => setUserCurrentPage(i + 1)}
                >
                  {i + 1}
                </button>
              ))}
              <button
                className="btn btn-secondary btn-xs"
                onClick={() => setUserCurrentPage(p => Math.min(userTotalPages, p + 1))}
                disabled={userCurrentPage === userTotalPages}
              >
                &rarr;
              </button>
            </nav>
          </div>
        </section>
      )}

      {/* Email Management Tab */}
      {activeTab === 'email' && (
        <section className="bg-white rounded-2xl shadow-soft p-8">
          {/* Setup Instructions */}
          <div className="mb-8 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl">
            <h3 className="text-xl font-semibold text-blue-800 mb-4">
              🔗 Email Service Setup Instructions
            </h3>

            <div className="space-y-4 text-blue-900">
              <div className="bg-white p-4 rounded-lg border border-blue-100">
                <h4 className="font-semibold text-blue-800 mb-2">
                  Step 1: Configure Your Email Provider
                </h4>
                <ol className="list-decimal list-inside space-y-1 text-sm">
                  <li>Choose your email provider from the supported options:</li>
                  <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
                    <li>
                      <strong>MailerLite:</strong>{' '}
                      <a
                        href="https://app.mailerlite.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 underline"
                      >
                        Get API Key
                      </a>
                    </li>
                    <li>
                      <strong>Mailchimp:</strong>{' '}
                      <a
                        href="https://admin.mailchimp.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 underline"
                      >
                        Get API Key
                      </a>
                    </li>
                    <li>
                      <strong>SendGrid:</strong>{' '}
                      <a
                        href="https://app.sendgrid.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 underline"
                      >
                        Get API Key
                      </a>
                    </li>
                    <li>
                      <strong>ConvertKit:</strong>{' '}
                      <a
                        href="https://app.convertkit.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 underline"
                      >
                        Get API Key
                      </a>
                    </li>
                    <li>
                      <strong>ActiveCampaign:</strong>{' '}
                      <a
                        href="https://www.activecampaign.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 underline"
                      >
                        Get API Key
                      </a>
                    </li>
                    <li>
                      <strong>Klaviyo:</strong>{' '}
                      <a
                        href="https://www.klaviyo.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 underline"
                      >
                        Get API Key
                      </a>
                    </li>
                    <li>
                      <strong>GetResponse:</strong>{' '}
                      <a
                        href="https://app.getresponse.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 underline"
                      >
                        Get API Key
                      </a>
                    </li>
                  </ul>
                  <li>Follow your provider's documentation to get your API key</li>
                </ol>
              </div>

              <div className="bg-white p-4 rounded-lg border border-blue-100">
                <h4 className="font-semibold text-blue-800 mb-2">
                  Step 2: Add Configuration to Environment
                </h4>
                <ol className="list-decimal list-inside space-y-1 text-sm">
                  <li>
                    Open your <code className="bg-gray-100 px-1 rounded">backend/.env</code> file
                  </li>
                  <li>Add these lines:</li>
                  <div className="bg-gray-100 p-2 rounded mt-1 font-mono text-xs">
                    <div>EMAIL_PROVIDER=mailerlite</div>
                    <div>EMAIL_API_KEY=your_api_key_here</div>
                    <div>DEFAULT_EMAIL_GROUP_ID=your_group_id</div>
                  </div>
                  <li>
                    Replace <code className="bg-gray-100 px-1 rounded">your_api_key_here</code> with
                    your actual API key
                  </li>
                  <li>
                    Set <code className="bg-gray-100 px-1 rounded">EMAIL_PROVIDER</code> to your
                    chosen provider (mailerlite, mailchimp, sendgrid, etc.)
                  </li>
                  <li>Save the file and restart your server</li>
                </ol>
              </div>

              <div className="bg-white p-4 rounded-lg border border-blue-100">
                <h4 className="font-semibold text-blue-800 mb-2">Step 3: Verify Connection</h4>
                <p className="text-sm">
                  Once you've added the configuration, the connection status below should show
                  "[Your Provider] Connected" in green.
                </p>
              </div>

              <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                <h4 className="font-semibold text-yellow-800 mb-2">⚠️ Important Notes</h4>
                <ul className="list-disc list-inside space-y-1 text-sm text-yellow-800">
                  <li>Keep your API key secure and never commit it to version control</li>
                  <li>The API key has full access to your MailerLite account</li>
                  <li>You can revoke the key anytime from your MailerLite dashboard</li>
                  <li>For production, use environment variables on your server</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h3 className="text-lg font-semibold text-blue-800 mb-2">
              Email Marketing & Newsletter Management
            </h3>
            <p className="text-blue-700">
              Manage subscribers, create campaigns, and send incident alerts through MailerLite
              integration.
            </p>
          </div>

          {/* Import EmailManager component */}
          <div className="space-y-6">
            <EmailManager />
          </div>
        </section>
      )}

      {/* Branding Configuration Tab - Available to all admins */}
      {activeTab === 'branding-config' && (
        <section className="bg-white rounded-2xl shadow-soft p-8">
          <BrandingConfigManager />
        </section>
      )}

      {/* Upload Configuration Tab - Super admin only */}
      {activeTab === 'upload-config' && user && user.isSuperAdmin && (
        <section className="bg-white rounded-2xl shadow-soft p-8">
          <UploadConfigManager />
        </section>
      )}

      {/* Super Admin Tabs - Only show if user is super admin */}
      {user && user.isSuperAdmin && (
        <>
          {/* Configuration Tab */}
          {activeTab === 'config' && (
            <section className="bg-white rounded-2xl shadow-soft p-8">
              <p className="text-gray-600 mb-6">
                Super admin configuration options will be available here.
              </p>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-yellow-800">Configuration management coming soon...</p>
              </div>
            </section>
          )}

          {/* Area Configuration Tab */}
          {activeTab === 'area-config' && (
            <section className="bg-white rounded-2xl shadow-soft p-8">
              <AreaConfigManager onActiveAreaChange={onActiveAreaChange} />
            </section>
          )}

          {/* System Stats Tab */}
          {activeTab === 'stats' && (
            <section className="bg-white rounded-2xl shadow-soft p-8">
              <p className="text-gray-600 mb-6">
                System performance and analytics will be displayed here.
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-blue-800">System statistics coming soon...</p>
              </div>
            </section>
          )}

          {/* Super User Management Tab */}
          {activeTab === 'super-users' && (
            <section className="bg-white rounded-2xl shadow-soft p-8">
              <p className="text-gray-600 mb-6">Manage super admin privileges and system access.</p>
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <p className="text-purple-800">Super user management coming soon...</p>
              </div>
            </section>
          )}

          {/* Database Management Tab */}
          {activeTab === 'database-management' && <DatabaseManagementTab user={user} />}

          {/* Error Logs Tab */}
          {activeTab === 'error-logs' && (
            <section className="bg-white rounded-2xl shadow-soft p-8">
              <ErrorLogViewer />
            </section>
          )}

          {/* Logs Analytics Dashboard Tab */}
          {activeTab === 'logs-analytics' && (
            <section className="bg-white rounded-2xl shadow-soft p-8">
              <AnalyticsPage />
            </section>
          )}

          {/* System Info Tab - Super Admin Only */}
          {activeTab === 'system-info' && user && user.isSuperAdmin && (
            <section className="bg-white rounded-2xl shadow-soft p-8">
              <SystemInfoPanel />
            </section>
          )}
        </>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-large max-w-2xl w-full p-6 relative">
            <button
              type="button"
              onClick={handleImportCancel}
              aria-label="Close import modal"
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 text-2xl bg-transparent border-none cursor-pointer"
            >
              ×
            </button>
            <h3 className="text-xl font-semibold text-primary-800 mb-4">Import Reports</h3>
            <form onSubmit={handleImportSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select CSV File
                </label>
                <input
                  type="file"
                  accept=".csv,text/csv,application/csv"
                  onChange={handleImportFileChange}
                  className="form-input"
                />
              </div>
              {importPreview.length > 0 && (
                <div
                  style={{
                    maxHeight: 300,
                    overflow: 'auto',
                    marginBottom: '1em',
                    border: '1px solid #eee',
                    borderRadius: 4,
                  }}
                >
                  <h4 style={{ margin: '0.5em 0' }}>Preview Data to Import</h4>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.95em' }}>
                    <thead>
                      <tr>
                        {importHeaders.map(h => (
                          <th
                            key={h}
                            style={{
                              background: '#f4f6fa',
                              border: '1px solid #e0e0e0',
                              padding: '0.4em',
                            }}
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {importPreview.slice(0, 10).map((row, i) => (
                        <tr key={i}>
                          {importHeaders.map(h => (
                            <td key={h} style={{ border: '1px solid #e0e0e0', padding: '0.4em' }}>
                              {row[h]}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {importPreview.length > 10 && (
                    <div style={{ fontSize: '0.85em', color: '#888', margin: '0.5em 0' }}>
                      Showing first 10 rows of {importPreview.length} total.
                    </div>
                  )}
                </div>
              )}
              {importStatus.loading && (
                <div className="mb-4 p-2 bg-blue-100 text-blue-800 rounded">
                  Importing reports...
                </div>
              )}
              {importStatus.message && (
                <div className="mb-4 p-2 bg-green-100 text-green-800 rounded">
                  {importStatus.message}
                </div>
              )}
              {importStatus.error && (
                <div className="mb-4 p-2 bg-red-100 text-red-800 rounded">{importStatus.error}</div>
              )}
              <div className="flex gap-3 justify-end">
                <button type="button" onClick={handleImportCancel} className="btn btn-secondary">
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={importStatus.loading || !importFile}
                >
                  Next
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {showConfirmImport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-large max-w-md w-full p-6">
            <h3 className="text-xl font-semibold text-primary-800 mb-4">Confirm Import</h3>
            <p>Are you sure you want to import {importPreview.length} reports?</p>

            <div className="mb-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={importAsApproved}
                  onChange={e => setImportAsApproved(e.target.checked)}
                  className="mr-2"
                />
                <span className="text-sm">Import reports as approved</span>
              </label>
              <p className="text-xs text-gray-600 mt-1">
                If checked, all imported reports will be automatically approved and marked as
                approved by you.
              </p>
            </div>

            <div className="flex gap-3 justify-end mt-4">
              <button type="button" onClick={handleImportCancel} className="btn btn-secondary">
                Cancel
              </button>
              <button type="button" onClick={handleConfirmImport} className="btn btn-success">
                Import Reports
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Export Options Modal */}
      {showExportOptions && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-large max-w-md w-full p-6">
            <h3 className="text-xl font-semibold text-primary-800 mb-4">Export Reports</h3>
            <p className="mb-4">Choose how you want to export the reports:</p>

            <div className="space-y-3">
              <button
                onClick={() => {
                  exportReportsToCSV(false);
                  setShowExportOptions(false);
                }}
                className="w-full btn btn-primary text-left"
              >
                <div className="font-semibold">CSV Only</div>
                <div className="text-sm text-gray-600">Export reports data as CSV file</div>
              </button>

              <button
                onClick={() => {
                  exportReportsToCSV(true);
                  setShowExportOptions(false);
                }}
                className="w-full btn btn-success text-left"
              >
                <div className="font-semibold">CSV + Images</div>
                <div className="text-sm text-gray-600">
                  Export CSV with all images in organized folders
                </div>
              </button>
            </div>

            <div className="flex gap-3 justify-end mt-6">
              <button
                type="button"
                onClick={() => setShowExportOptions(false)}
                className="btn btn-secondary"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* User Edit Modal */}
      {editingUser && (
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="edit-user-modal-title"
          tabIndex="-1"
        >
          <form
            onSubmit={saveEditUser}
            className="bg-white rounded-lg p-6 box-border shadow-lg relative"
          >
            <button
              type="button"
              onClick={cancelEditUser}
              aria-label="Close edit modal"
              className="absolute top-4 right-4 bg-transparent border-none text-2xl cursor-pointer text-gray-500 leading-none"
            >
              ×
            </button>
            <h3 id="edit-user-modal-title" className="text-xl font-bold mb-4">
              Edit User
            </h3>
            <div className="mb-4">
              <label className="block text-sm font-bold mb-1">
                Name
                <br />
                <input
                  name="name"
                  type="text"
                  value={userEditForm.name}
                  onChange={handleUserEditFormChange}
                  className="w-full p-2 border border-gray-300 rounded-md"
                  required
                  autoFocus
                />
              </label>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-bold mb-1">
                Email
                <br />
                <input
                  name="email"
                  type="email"
                  value={userEditForm.email}
                  onChange={handleUserEditFormChange}
                  className="w-full p-2 border border-gray-300 rounded-md"
                  required
                />
              </label>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-bold mb-1">
                Phone
                <br />
                <input
                  name="number"
                  type="text"
                  value={userEditForm.number}
                  onChange={handleUserEditFormChange}
                  className="w-full p-2 border border-gray-300 rounded-md"
                />
              </label>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-bold flex items-center">
                <input
                  name="isAdmin"
                  type="checkbox"
                  checked={userEditForm.isAdmin}
                  onChange={handleUserEditFormChange}
                  className="mr-2"
                />
                Admin User
              </label>
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={cancelEditUser}
                className="px-4 py-2 rounded-md bg-gray-300 text-gray-800 hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-400 transition"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 rounded-md bg-blue-700 text-white hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-400 transition"
              >
                Save
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Photo Preview Modal */}
      {previewModal.open && (
        <div className="fixed inset-0 bg-black bg-opacity-90 z-[60] flex items-center justify-center p-4">
          <div className="relative max-w-6xl max-h-full w-full h-full flex flex-col">
            {/* Close button */}
            <button
              onClick={closePreviewModal}
              className="absolute top-4 right-4 bg-black bg-opacity-50 text-white rounded-full w-10 h-10 flex items-center justify-center hover:bg-opacity-75 transition-colors z-10"
              aria-label="Close gallery"
            >
              ✕
            </button>

            {/* Navigation arrows */}
            {previewModal.photos.length > 1 && (
              <>
                <button
                  onClick={prevPhoto}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white rounded-full w-12 h-12 flex items-center justify-center hover:bg-opacity-75 transition-colors z-10"
                  aria-label="Previous photo"
                >
                  ‹
                </button>
                <button
                  onClick={nextPhoto}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white rounded-full w-12 h-12 flex items-center justify-center hover:bg-opacity-75 transition-colors z-10"
                  aria-label="Next photo"
                >
                  ›
                </button>
              </>
            )}

            {/* Photo counter */}
            {previewModal.photos.length > 1 && (
              <div className="absolute top-4 left-4 bg-black bg-opacity-50 text-white px-3 py-1 rounded-full text-sm z-10">
                {previewModal.currentIndex + 1} / {previewModal.photos.length}
              </div>
            )}

            {/* Main image */}
            <div className="flex-1 flex items-center justify-center">
              <img
                src={previewModal.url}
                alt={`${previewModal.currentIndex + 1}${previewModal.caption ? `: ${previewModal.caption}` : ''}`}
                className="max-w-full max-h-full object-contain rounded-lg"
              />
            </div>

            {/* Caption */}
            {previewModal.caption && (
              <div className="absolute bottom-4 left-4 right-4 bg-black bg-opacity-50 text-white p-4 rounded-lg">
                <p className="text-sm">{previewModal.caption}</p>
              </div>
            )}

            {/* Thumbnail navigation */}
            {previewModal.photos.length > 1 && (
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2 bg-black bg-opacity-50 p-2 rounded-lg">
                {previewModal.photos.map((photo, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      setPreviewModal(prev => ({
                        ...prev,
                        currentIndex: index,
                        url: photo.url,
                        caption: photo.caption,
                      }));
                    }}
                    className={`w-12 h-12 rounded border-2 transition-all ${
                      index === previewModal.currentIndex
                        ? 'border-white opacity-100'
                        : 'border-gray-400 opacity-60 hover:opacity-80'
                    }`}
                  >
                    <img
                      src={photo.url}
                      alt={`Thumbnail ${index + 1}`}
                      className="w-full h-full object-cover rounded"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {confirmationModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-large max-w-md w-full p-6">
            <h3 className="text-xl font-semibold text-primary-800 mb-4">
              {confirmationModal.title}
            </h3>
            <p className="text-gray-700 mb-6">{confirmationModal.message}</p>
            <div className="flex gap-3 justify-end">
              <button onClick={closeConfirmationModal} className="btn btn-secondary">
                Cancel
              </button>
              <button onClick={handleConfirmAction} className="btn btn-primary">
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {editModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-large max-w-4xl w-full max-h-screen overflow-y-auto p-4 md:p-6 relative">
            {/* Header with title and navigation - Sticky & Mobile Responsive */}
            <div className="sticky top-0 bg-white z-20 pb-4 mb-6 border-b border-gray-200">
              {/* Top Row - Title and Close Button */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg md:text-2xl font-bold text-primary-800 truncate">
                    Edit Incident Report
                    {editReport && editReport.id && (
                      <span className="text-sm md:text-lg font-normal text-gray-600 ml-1 md:ml-2">
                        (SWARM #{editReport.id})
                      </span>
                    )}
                  </h3>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs md:text-sm text-gray-600 bg-gray-100 px-2 py-1 rounded-full">
                      {editReportIndex + 1} of {reports.length}
                    </span>
                  </div>
                </div>
                {/* Close button in header */}
                <button
                  type="button"
                  onClick={closeEditModal}
                  aria-label="Close edit modal"
                  className="text-gray-400 hover:text-gray-700 text-xl md:text-2xl bg-white rounded-full w-8 h-8 md:w-8 md:h-8 flex items-center justify-center shadow-sm hover:bg-gray-50 transition-colors flex-shrink-0 ml-2"
                  style={{ background: 'white', border: 'none', cursor: 'pointer' }}
                >
                  ×
                </button>
              </div>

              {/* Timestamps Row - Mobile Responsive */}
              <div className="mb-3">
                <div className="text-xs text-gray-500">
                  <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                    <div>
                      <span className="font-medium">Created:</span>{' '}
                      {editReport?.createdAt
                        ? formatTimeForDisplay(editReport.createdAt)
                        : 'Not available'}
                    </div>
                    {editReport?.updatedAt && (
                      <div>
                        <span className="font-medium">Updated:</span>{' '}
                        {formatTimeForDisplay(editReport.updatedAt)}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Navigation Section - Mobile Responsive */}
              <div className="space-y-2 md:space-y-0">
                {/* Mobile: Stacked rows, Desktop: Single compact row */}
                <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-2 md:gap-3">
                  {/* Report counter - only show on mobile */}
                  <span className="text-sm text-gray-600 md:hidden">
                    Report {editReportIndex + 1} of {reports.length}
                  </span>

                  {/* Button groups - compact on desktop */}
                  <div className="flex flex-col md:flex-row gap-2 md:gap-2">
                    {/* Navigation buttons */}
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={navigateToPreviousReport}
                        disabled={editReportIndex === 0}
                        className="btn btn-secondary btn-small flex-1 md:flex-none text-xs md:text-sm px-3 md:px-3"
                      >
                        ← Previous
                      </button>
                      <button
                        type="button"
                        onClick={navigateToNextReport}
                        disabled={editReportIndex === reports.length - 1}
                        className="btn btn-secondary btn-small flex-1 md:flex-none text-xs md:text-sm px-3 md:px-3"
                      >
                        Next →
                      </button>
                    </div>

                    {/* Save buttons */}
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => handleEditSave(false)} // false = don't navigate to next
                        disabled={editLoading}
                        className="btn btn-secondary btn-small flex-1 md:flex-none text-xs md:text-sm px-3 md:px-3"
                      >
                        Save & Stay
                      </button>
                      <button
                        type="button"
                        onClick={() => handleEditSave(true)} // true = navigate to next
                        disabled={editLoading}
                        className="btn btn-primary btn-small flex-1 md:flex-none text-xs md:text-sm px-3 md:px-3"
                      >
                        Save & Next
                      </button>
                    </div>
                  </div>

                  {/* Approve button - separated approval action */}
                  {!editForm.isApproved && (
                    <div className="flex justify-center md:justify-end mt-2 md:mt-0">
                      <div className="border-l-2 border-green-200 pl-3 md:pl-4">
                        <button
                          type="button"
                          onClick={() => handleLocalApproveReport(editReport.id)}
                          disabled={editLoading}
                          className="btn btn-success btn-small text-xs md:text-sm px-4 md:px-4 border-2 border-green-500 text-green-800 bg-green-100 font-bold shadow-sm hover:bg-green-200 focus:ring-2 focus:ring-green-400 transition-colors"
                          title="Approve this report - This will mark the report as approved"
                        >
                          ✓ Approve Report
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Revoke button - separated destructive action */}
                  {editForm.isApproved && (
                    <div className="flex justify-center md:justify-end mt-2 md:mt-0">
                      <div className="border-l-2 border-red-200 pl-3 md:pl-4">
                        <button
                          type="button"
                          onClick={() => handleLocalRevokeApproval(editReport.id)}
                          disabled={editLoading}
                          className="btn btn-error btn-small text-xs md:text-sm px-4 md:px-4 border-2 border-red-500 text-red-800 bg-red-100 font-bold shadow-sm hover:bg-red-200 focus:ring-2 focus:ring-red-400 transition-colors"
                          title="Revoke approval for this report - This will make the report pending again"
                        >
                          ⚠️ Revoke Approval
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <form
              onSubmit={e => {
                e.preventDefault();
                // Form submission is now handled by header navigation buttons
              }}
              className="space-y-6"
            >
              {/* Progress Indicator - Navigation moved to sticky header */}
              <div className="mb-4">
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${((editReportIndex + 1) / reports.length) * 100}%` }}
                  ></div>
                </div>
              </div>

              {/* Incident Details Section */}
              <div className="form-section">
                <h3 className="text-xl font-semibold text-primary-800 mb-4">Incident Details</h3>

                {/* Photos Section */}
                <div className="form-group mb-6">
                  <label className="form-label">Photos (optional):</label>
                  {(Array.isArray(editImages) && editImages.length > 0) ||
                  (Array.isArray(editImageFiles) && editImageFiles.length > 0) ? (
                    <div className="flex flex-wrap gap-4 mb-2">
                      {/* Show all images in a unified way - existing images first, then new files */}
                      {(() => {
                        const allImages = [];

                        // First, add all existing images (non-blob URLs)
                        editImages.forEach((img, idx) => {
                          if (img && !img.startsWith('blob:') && !img.startsWith('data:')) {
                            allImages.push({
                              type: 'existing',
                              src: getSafeFileUrl(img),
                              key: `existing-${idx}`,
                              index: idx,
                            });
                          }
                        });

                        // Then, add all new files
                        editImageFiles.forEach((file, idx) => {
                          if (file) {
                            allImages.push({
                              type: 'file',
                              src: URL.createObjectURL(file),
                              key: `file-${idx}`,
                              index: idx,
                            });
                          }
                        });

                        console.log('🔍 AdminPanel Display: All images to show:', allImages.length);

                        return allImages.map((img, displayIdx) => {
                          return (
                            <div
                              key={img.key}
                              className="relative group flex flex-col items-center"
                            >
                              <img
                                src={img.src}
                                alt={`Photo ${displayIdx + 1}`}
                                className="w-24 h-24 object-cover rounded border mb-1"
                              />
                              <div className="flex gap-1 mb-1">
                                <button
                                  type="button"
                                  onClick={() => {
                                    openPreviewModal(
                                      img.src,
                                      '',
                                      allImages.map(p => ({
                                        url: p.src,
                                        caption: '',
                                      })),
                                      displayIdx,
                                    );
                                  }}
                                  className="btn btn-xs btn-secondary"
                                >
                                  View
                                </button>
                                <button
                                  type="button"
                                  onClick={() => openImageEditor(img.index, img.type)}
                                  className="btn btn-xs btn-primary"
                                >
                                  Edit
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleRemoveImage(img.index)}
                                  className="btn btn-xs btn-error"
                                  aria-label="Remove image"
                                >
                                  ×
                                </button>
                              </div>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-24 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 mb-2">
                      <img
                        src={getIncidentTypeIconUrl(editForm.incidentType, 48)}
                        alt={`${formatIncidentType(editForm.incidentType)} incident`}
                        className="w-12 h-12 opacity-60"
                      />
                      <span className="ml-3 text-sm text-gray-500">No photos uploaded</span>
                    </div>
                  )}
                  {/* Dropzone for uploading new images */}
                  <div
                    {...getRootProps()}
                    className={`border-2 border-dashed rounded-lg p-4 flex flex-col items-center justify-center transition mb-2 ${isUploading ? 'border-blue-400 bg-blue-50 cursor-wait' : 'border-primary-300 cursor-pointer hover:bg-primary-50'}`}
                    style={{ minHeight: 80 }}
                    aria-label="Image upload dropzone"
                  >
                    <input {...getInputProps()} disabled={isUploading} />
                    {isUploading ? (
                      <div className="text-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
                        <span className="text-sm font-medium text-blue-700">
                          Processing images...
                        </span>
                      </div>
                    ) : (
                      <>
                        <span className="text-lg font-semibold text-primary-700 mb-2">
                          Drag & drop multiple images here, or click to select
                        </span>
                        <span className="text-sm text-gray-500">
                          (JPG, PNG, GIF, up to 10MB each - Multiple files supported)
                        </span>
                      </>
                    )}
                  </div>
                </div>

                {/* Incident Type */}
                <div className="form-group">
                  <label htmlFor="edit-incidentType" className="form-label required">
                    Incident Type:
                  </label>
                  <select
                    id="edit-incidentType"
                    name="incidentType"
                    value={editForm.incidentType || ''}
                    onChange={handleEditFormChange}
                    required
                    className="form-input"
                  >
                    <option value="">Select incident type...</option>
                    {INCIDENT_TYPE_GROUPS.map(group => (
                      <optgroup key={group.label} label={group.label}>
                        {group.options.map(type => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="edit-location" className="form-label required">
                    Location:
                  </label>
                  <input
                    type="text"
                    id="edit-location"
                    name="location"
                    value={editForm.location || ''}
                    onChange={handleEditFormChange}
                    required
                    placeholder="e.g. Main St & 2nd Ave or coordinates"
                    className="form-input"
                  />
                  <div className="flex gap-2 mt-2">
                    <button
                      type="button"
                      onClick={openMapPicker}
                      className="btn btn-inline"
                      aria-label="Select location on map"
                    >
                      Select on Map
                    </button>
                  </div>
                </div>

                {editForm.coordinates && (
                  <div className="form-group">
                    <span className="text-sm text-gray-600">
                      <strong>Coordinates:</strong>{' '}
                      {typeof editForm.coordinates === 'string'
                        ? editForm.coordinates
                        : `${editForm.coordinates.lat}, ${editForm.coordinates.lng}`}
                    </span>
                  </div>
                )}

                <div className="form-group">
                  <label htmlFor="edit-description" className="form-label required">
                    Description:
                  </label>
                  <textarea
                    id="edit-description"
                    name="description"
                    value={editForm.description || ''}
                    onChange={handleEditFormChange}
                    required
                    placeholder="Describe the incident..."
                    className="form-input"
                    rows="4"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="edit-estimatedIncidentTime" className="form-label required">
                    Estimated Time of Incident:
                  </label>
                  <div className="flex items-center gap-3 mb-2">
                    <input
                      type="checkbox"
                      id="edit-timeUnknown"
                      name="timeUnknown"
                      checked={editForm.timeUnknown || false}
                      onChange={handleEditFormChange}
                      className="form-checkbox"
                    />
                    <label htmlFor="edit-timeUnknown" className="text-sm text-gray-600">
                      Time is unknown (date only)
                    </label>
                  </div>
                  <div className="flex items-center gap-3 mb-2">
                    <input
                      type="checkbox"
                      id="edit-isDateRange"
                      name="isDateRange"
                      checked={editForm.isDateRange || false}
                      onChange={handleEditFormChange}
                      className="form-checkbox"
                    />
                    <label htmlFor="edit-isDateRange" className="text-sm text-gray-600">
                      Date is a range (max 5 days)
                    </label>
                  </div>
                  {editForm.isDateRange ? (
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Range Start Date
                        </label>
                        <input
                          type="date"
                          id="edit-incidentDateRangeStart"
                          name="incidentDateRangeStart"
                          value={editForm.incidentDateRangeStart || ''}
                          onChange={handleEditFormChange}
                          className="form-input"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Range End Date
                        </label>
                        <input
                          type="date"
                          id="edit-incidentDateRangeEnd"
                          name="incidentDateRangeEnd"
                          value={editForm.incidentDateRangeEnd || ''}
                          onChange={handleEditFormChange}
                          className="form-input"
                        />
                      </div>
                      <div className="form-help-text">
                        Select the date range when the incident occurred (maximum 5 days)
                      </div>
                    </div>
                  ) : (
                    <>
                      <input
                        type={editForm.timeUnknown ? 'date' : 'datetime-local'}
                        id="edit-estimatedIncidentTime"
                        name="estimatedIncidentTime"
                        value={editForm.estimatedIncidentTime || ''}
                        onChange={handleEditFormChange}
                        className="form-input"
                      />
                      <div className="form-help-text">
                        {editForm.timeUnknown
                          ? 'Select the date when the incident occurred (time is unknown)'
                          : 'Select the date and time when the incident occurred'}
                      </div>
                    </>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="edit-witnessedIncident" className="form-label required">
                    Did you witness the incident occur?
                  </label>
                  <select
                    id="edit-witnessedIncident"
                    name="witnessedIncident"
                    value={editForm.witnessedIncident || ''}
                    onChange={handleEditFormChange}
                    className="form-input"
                  >
                    <option value="">Select...</option>
                    <option value="yes">Yes, I witnessed it</option>
                    <option value="after">Witnessed after the incident</option>
                    <option value="no">No, I was told about it</option>
                    <option value="unsure">Unsure</option>
                  </select>
                </div>

                {editForm.witnessedIncident === 'after' && (
                  <div className="form-group">
                    <label htmlFor="edit-evidenceDescription" className="form-label">
                      Please describe the evidence you observed
                    </label>
                    <textarea
                      id="edit-evidenceDescription"
                      name="evidenceDescription"
                      value={editForm.evidenceDescription || ''}
                      onChange={handleEditFormChange}
                      className="form-input"
                      placeholder="e.g., Damaged traffic light pole, skid marks on road, debris scattered across intersection"
                      rows="3"
                    />
                    <div className="form-help-text">
                      Describe what evidence you saw that indicates an incident occurred
                    </div>
                  </div>
                )}

                <div className="form-group">
                  <label htmlFor="edit-injuries" className="form-label">
                    Were there any injuries in this incident?
                  </label>
                  <select
                    id="edit-injuries"
                    name="injuries"
                    value={editForm.injuries || ''}
                    onChange={handleEditFormChange}
                    className="form-input"
                  >
                    <option value="">Select...</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                    <option value="unsure">Unsure</option>
                  </select>
                </div>

                {editForm.injuries === 'yes' && (
                  <div className="form-group">
                    <label htmlFor="edit-injuryCount" className="form-label">
                      How many people were injured?
                    </label>
                    <input
                      type="number"
                      id="edit-injuryCount"
                      name="injuryCount"
                      value={editForm.injuryCount || ''}
                      onChange={handleEditFormChange}
                      min="1"
                      max="50"
                      placeholder="e.g. 2"
                      className="form-input"
                    />
                    <label htmlFor="edit-injuryNotes" className="form-label mt-4">
                      Please provide details about the injuries (optional):
                    </label>
                    <textarea
                      id="edit-injuryNotes"
                      name="injuryNotes"
                      value={editForm.injuryNotes || ''}
                      onChange={handleEditFormChange}
                      placeholder="Describe the nature of the injuries, who was injured, etc."
                      className="form-input"
                      rows="3"
                    />
                  </div>
                )}
              </div>

              {/* Emergency Services Section */}
              <div className="form-section">
                <h3 className="text-lg font-semibold text-primary-800 mb-4">Emergency Services</h3>

                <div className="form-group">
                  <label htmlFor="edit-emergencyServicesAttended" className="form-label">
                    Did police and/or emergency services attend?
                  </label>
                  <select
                    id="edit-emergencyServicesAttended"
                    name="emergencyServicesAttended"
                    value={editForm.emergencyServicesAttended || ''}
                    onChange={handleEditFormChange}
                    className="form-input"
                  >
                    <option value="">Select...</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                    <option value="dont-know">Don't know</option>
                  </select>
                </div>

                {editForm.emergencyServicesAttended === 'yes' && (
                  <div className="form-section-nested">
                    <div className="form-group">
                      <label className="form-label">Which services attended?</label>
                      <div className="space-y-2">
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            name="policeAttended"
                            checked={editForm.policeAttended || false}
                            onChange={e =>
                              setEditForm(prev => ({ ...prev, policeAttended: e.target.checked }))
                            }
                            className="mr-2"
                          />
                          Police
                        </label>
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            name="ambulanceAttended"
                            checked={editForm.ambulanceAttended || false}
                            onChange={e =>
                              setEditForm(prev => ({
                                ...prev,
                                ambulanceAttended: e.target.checked,
                              }))
                            }
                            className="mr-2"
                          />
                          Ambulance/Paramedics
                        </label>
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            name="fireBrigadeAttended"
                            checked={editForm.fireBrigadeAttended || false}
                            onChange={e =>
                              setEditForm(prev => ({
                                ...prev,
                                fireBrigadeAttended: e.target.checked,
                              }))
                            }
                            className="mr-2"
                          />
                          Fire Brigade
                        </label>
                      </div>
                    </div>

                    {editForm.policeAttended && (
                      <>
                        <div className="form-group">
                          <label htmlFor="edit-policeReportTime" className="form-label">
                            Time of Report:
                          </label>
                          <input
                            type="datetime-local"
                            id="edit-policeReportTime"
                            name="policeReportTime"
                            value={editForm.policeReportTime || ''}
                            onChange={handleEditFormChange}
                            className="form-input"
                          />
                        </div>
                        <div className="form-group">
                          <label htmlFor="edit-policeOfficerName" className="form-label">
                            Name of Officer (optional):
                          </label>
                          <input
                            type="text"
                            id="edit-policeOfficerName"
                            name="policeOfficerName"
                            value={editForm.policeOfficerName || ''}
                            onChange={handleEditFormChange}
                            placeholder="e.g. Constable Smith"
                            className="form-input"
                          />
                        </div>
                        <div className="form-group">
                          <label htmlFor="edit-policeReportNumber" className="form-label">
                            Police Report Number (optional):
                          </label>
                          <input
                            type="text"
                            id="edit-policeReportNumber"
                            name="policeReportNumber"
                            value={editForm.policeReportNumber || ''}
                            onChange={handleEditFormChange}
                            placeholder="e.g. 12345678"
                            className="form-input"
                          />
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Reporter Details Section */}
              <div className="form-section">
                <h3 className="text-lg font-semibold text-primary-800 mb-4">Reporter Details</h3>

                <div className="form-group">
                  <label htmlFor="edit-reporterName" className="form-label required">
                    Reporter Name:
                  </label>
                  <input
                    type="text"
                    id="edit-reporterName"
                    name="reporterName"
                    value={editForm.reporterName || ''}
                    onChange={handleEditFormChange}
                    placeholder="e.g. Jane Citizen"
                    className="form-input"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="edit-reporterNumber" className="form-label">
                    Contact Number (optional):
                  </label>
                  <input
                    type="text"
                    id="edit-reporterNumber"
                    name="reporterNumber"
                    value={editForm.reporterNumber || ''}
                    onChange={handleEditFormChange}
                    placeholder="e.g. 0412 345 678"
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="edit-reporterEmail" className="form-label required">
                    Email:
                  </label>
                  <input
                    type="email"
                    id="edit-reporterEmail"
                    name="reporterEmail"
                    value={editForm.reporterEmail || ''}
                    onChange={handleEditFormChange}
                    placeholder="e.g. jane@email.com"
                    className="form-input"
                    required
                  />
                </div>
              </div>

              {/* Validation errors (if any) */}
              {Object.keys(editFormErrors).length > 0 && (
                <div
                  ref={errorSummaryRef}
                  className="validation-summary mb-4"
                  tabIndex={-1}
                  role="alert"
                  aria-live="assertive"
                >
                  <h3>Please fix the following errors:</h3>
                  <ul>
                    {Object.entries(editFormErrors).map(([fieldName, error]) => (
                      <li key={fieldName}>
                        <strong>
                          {fieldName
                            .replace(/([A-Z])/g, ' $1')
                            .replace(/^./, str => str.toUpperCase())}
                          :
                        </strong>{' '}
                        {error}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Success and Error Messages */}
              {editForm.success && (
                <div className="p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {editForm.success}
                  </div>
                </div>
              )}

              {editForm.error && (
                <div className="p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {editForm.error}
                  </div>
                </div>
              )}

              {/* Upload Progress Indicator */}
              {isUploading && (
                <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-blue-800">Uploading Images...</span>
                    <span className="text-sm text-blue-600">{uploadProgress}%</span>
                  </div>
                  <div className="w-full bg-blue-200 rounded-full h-2">
                    <div
                      className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                  <p className="text-xs text-blue-600 mt-1">
                    Please wait while your images are being uploaded...
                  </p>
                </div>
              )}
            </form>
          </div>
        </div>
      )}
      {imageEditorOpen && (
        <ImageEditor
          image={imageEditorSrc}
          onSave={handleImageEditorSave}
          onCancel={handleImageEditorCancel}
          originalAspectRatio={(() => {
            // Get the aspect ratio from photo metadata if available
            if (
              editForm.photoMetadata &&
              Array.isArray(editForm.photoMetadata) &&
              imageEditorIndex !== null
            ) {
              const photoMeta = editForm.photoMetadata[imageEditorIndex];
              if (
                photoMeta &&
                photoMeta.aspectRatio !== null &&
                photoMeta.aspectRatio !== undefined
              ) {
                return photoMeta.aspectRatio;
              }
            }
            return null;
          })()}
        />
      )}
      <MapPicker
        isOpen={showMapPicker}
        onClose={closeMapPicker}
        onConfirm={handleMapPickerConfirm}
        initialLocation={
          editForm && editForm.coordinates && editForm.location
            ? {
                ...(() => {
                  // Handle both string and object formats for coordinates
                  let lat, lng;
                  if (
                    typeof editForm.coordinates === 'string' &&
                    editForm.coordinates.trim() !== ''
                  ) {
                    const match = editForm.coordinates.match(/(-?\d+\.?\d*),\s*(-?\d+\.?\d*)/);
                    if (!match) return null;
                    lat = parseFloat(match[1]);
                    lng = parseFloat(match[2]);
                  } else if (
                    editForm.coordinates &&
                    typeof editForm.coordinates === 'object' &&
                    editForm.coordinates.lat !== undefined &&
                    editForm.coordinates.lng !== undefined
                  ) {
                    lat = editForm.coordinates.lat;
                    lng = editForm.coordinates.lng;
                  } else {
                    return null;
                  }

                  // Validate that we have valid numbers
                  if (
                    typeof lat !== 'number' ||
                    typeof lng !== 'number' ||
                    isNaN(lat) ||
                    isNaN(lng)
                  ) {
                    console.warn(
                      'Invalid coordinates for MapPicker in AdminPanel:',
                      editForm.coordinates,
                    );
                    return null;
                  }

                  return { lat, lng };
                })(),
                address: editForm.location,
                coords:
                  typeof editForm.coordinates === 'string'
                    ? editForm.coordinates
                    : `${editForm.coordinates?.lat}, ${editForm.coordinates?.lng}`,
              }
            : null
        }
        activeArea={activeArea}
        description={editForm?.description || ''}
      />
      {activeTab === 'analytics' && (
        <section className="bg-white rounded-2xl shadow-soft p-8">
          <AdminAnalytics reports={reports} user={user} />
        </section>
      )}

      {/* Password Reset Modal */}
      {passwordResetModal.show && (
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="password-reset-modal-title"
          tabIndex="-1"
        >
          <form
            onSubmit={handlePasswordResetSubmit}
            className="bg-white rounded-lg p-6 box-border shadow-lg relative max-w-md w-full mx-4"
          >
            <button
              type="button"
              onClick={closePasswordResetModal}
              aria-label="Close password reset modal"
              className="absolute top-4 right-4 bg-transparent border-none text-2xl cursor-pointer text-gray-500 leading-none"
            >
              ×
            </button>
            <h3 id="password-reset-modal-title" className="text-xl font-bold mb-4">
              Reset Password
            </h3>
            <p className="text-gray-600 mb-4">
              Reset password for user: <strong>{passwordResetModal.user?.name}</strong> (
              {passwordResetModal.user?.email})
            </p>

            <div className="mb-4">
              <label className="block text-sm font-bold mb-1">
                New Password
                <br />
                <input
                  name="newPassword"
                  type="password"
                  value={passwordResetModal.newPassword}
                  onChange={handlePasswordResetChange}
                  className="w-full p-2 border border-gray-300 rounded-md"
                  required
                  autoFocus
                  minLength="6"
                  placeholder="Enter new password (min 6 characters)"
                />
              </label>
            </div>

            {passwordResetModal.error && (
              <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                {passwordResetModal.error}
              </div>
            )}

            {passwordResetModal.success && (
              <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded">
                {passwordResetModal.success}
              </div>
            )}

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={closePasswordResetModal}
                className="px-4 py-2 rounded-md bg-gray-300 text-gray-800 hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-400 transition"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 rounded-md bg-yellow-600 text-white hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-yellow-400 transition"
                disabled={
                  !passwordResetModal.newPassword || passwordResetModal.newPassword.length < 6
                }
              >
                Reset Password
              </button>
            </div>
          </form>
        </div>
      )}
    </AdminLayout>
  );
}

export default AdminPanel;
